import React, {useState, useEffect, useRef} from 'react'
import {useHistory} from 'react-router-dom'
import Axios from 'axios'
import Board from '../home/board/Board'

// Global styles for animations, tap highlight removal, and dark scrollbars
const globalStyles = `
@import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;500;700&display=swap');

@keyframes spin { to { transform: rotate(360deg); } }
@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes slideInFromRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
* { -webkit-tap-highlight-color: transparent; }

/* Base font */
body, input, button, select, textarea {
  font-family: 'Lato', sans-serif;
  font-weight: 300;
}

/* Caret color matches text color */
input, textarea {
  caret-color: currentColor;
}

/* Dark mode scrollbar */
::-webkit-scrollbar { width: 8px; height: 8px; }
::-webkit-scrollbar-track { background: #1a1a1a; }
::-webkit-scrollbar-thumb { background: #444; border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: #555; }

/* Set menu scrollbar - darker track, lighter thumb, no rounded corners */
.set-menu-scroll::-webkit-scrollbar { width: 8px; }
.set-menu-scroll::-webkit-scrollbar-track { background: #1a1a1a; }
.set-menu-scroll::-webkit-scrollbar-thumb { background: #3f7cbf; border-radius: 0; }
.set-menu-scroll::-webkit-scrollbar-thumb:hover { background: #5290d0; }
.set-menu-scroll { scrollbar-color: #3f7cbf #1a1a1a; }

/* Hide native select appearance on mobile */
select { -webkit-appearance: none; -moz-appearance: none; appearance: none; }
`

// Detect if we're running locally or on production
const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

// Helper function to get backend URL
const getBackendUrl = () => {
  if (!isLocalDev) return 'https://moxtest.app'
  return `http://localhost:${__DEV_BACKEND_PORT__}`
}

// Fuzzy search helper - returns a score (lower is better match, -1 means no match)
// Handles typos by checking if query chars appear in order, with tolerance for missing/wrong chars
const fuzzyMatch = (query, target) => {
  const q = query.toLowerCase()
  const t = target.toLowerCase()

  // Exact substring match is best
  if (t.includes(q)) return 0

  // Check if all query characters appear in order (fuzzy match)
  let qi = 0
  let ti = 0
  let gaps = 0

  while (qi < q.length && ti < t.length) {
    if (q[qi] === t[ti]) {
      qi++
      ti++
    } else {
      ti++
      gaps++
    }
  }

  // If we matched all query chars, return score based on gaps
  if (qi === q.length) {
    return 1 + gaps
  }

  // Try with 1 character tolerance (allows for 1 typo)
  // Skip each char in query and see if rest matches
  for (let skip = 0; skip < q.length; skip++) {
    const qWithoutChar = q.slice(0, skip) + q.slice(skip + 1)
    if (qWithoutChar.length < 2) continue

    let qi2 = 0
    let ti2 = 0
    while (qi2 < qWithoutChar.length && ti2 < t.length) {
      if (qWithoutChar[qi2] === t[ti2]) {
        qi2++
      }
      ti2++
    }
    if (qi2 === qWithoutChar.length) {
      return 10 + skip // Typo match, lower priority
    }
  }

  return -1 // No match
}

// Card dimensions - maintain Scryfall aspect ratio (488x680)
const CARD_WIDTH_DESKTOP = 200
const CARD_HEIGHT_DESKTOP = Math.round((CARD_WIDTH_DESKTOP * 680) / 488)
const CARD_WIDTH_MOBILE = 300
const CARD_HEIGHT_MOBILE = Math.round((CARD_WIDTH_MOBILE * 680) / 488)

// Stacked layout - visible portion of each card (the "fanning" amount)
const STACK_VISIBLE_MOBILE = 48
const STACK_VISIBLE_DESKTOP = 32

// Card image component with loading/error handling
const CardImage = ({card, width, height, showFlip = true}) => {
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [showInfo, setShowInfo] = useState(false)
  const [useFallback, setUseFallback] = useState(false)
  const [flipped, setFlipped] = useState(false)

  // Check if this is a double-faced card (name contains " // ")
  const hasTwoFaces = card.name?.includes(' // ') || (card.card_faces && card.card_faces.length === 2)

  // State for fetched card_faces data (when not in metadata)
  const [fetchedCardFaces, setFetchedCardFaces] = useState(null)

  // Fetch card_faces from Scryfall if we have a transform card but no card_faces data
  const scryfallId = card.scryfallId || card.id

  // Robust Scryfall fallback. `card.scryfallUrl` is null whenever the metadata thought the
  // local image existed, so derive one from image_uris / scryfallId as a last resort. This
  // lets a card whose cached file is missing or corrupt still fall back to Scryfall.
  const fallbackUrl =
    card.scryfallUrl ||
    card.image_uris?.normal ||
    card.image_uris?.large ||
    card.card_faces?.[0]?.image_uris?.normal ||
    (scryfallId ? `https://api.scryfall.com/cards/${scryfallId}?format=image&version=normal` : null)

  useEffect(() => {
    if (hasTwoFaces && !card.card_faces) {
      console.log(`[CardImage] Transform card missing card_faces:`, {
        name: card.name,
        scryfallId,
        id: card.id,
        hasCardFaces: !!card.card_faces,
      })
    }
    if (hasTwoFaces && !card.card_faces && scryfallId && !fetchedCardFaces) {
      console.log(`[CardImage] Fetching card_faces for ${card.name} from Scryfall...`)
      fetch(`https://api.scryfall.com/cards/${card.scryfallId}`)
        .then(r => r.json())
        .then(data => {
          if (data.card_faces && data.card_faces.length >= 2) {
            console.log(`[CardImage] Got card_faces for ${card.name}:`, data.card_faces[1]?.image_uris?.normal)
            setFetchedCardFaces(data.card_faces)
          }
        })
        .catch(err => console.log(`[CardImage] Failed to fetch card_faces:`, err))
    }
  }, [hasTwoFaces, card.card_faces, scryfallId, card.name, fetchedCardFaces])

  // Use fetched card_faces if available, otherwise use from card prop
  const cardFaces = card.card_faces || fetchedCardFaces

  // Get Scryfall URL for back face (the reliable fallback)
  const scryfallBackUrl = cardFaces?.[1]?.image_uris?.normal || cardFaces?.[1]?.image_uris?.large

  // Back face URL: try local first (url + _back), fallback to Scryfall
  const backFaceUrl = hasTwoFaces
    ? (card.url ? `${card.url}_back` : null) || scryfallBackUrl
    : null
  const backFaceFallback = scryfallBackUrl

  // If card is marked as having no local image, use fallback immediately
  useEffect(() => {
    if (card.imageExists === false && fallbackUrl) {
      setUseFallback(true)
    }
  }, [card.imageExists, fallbackUrl])

  // Retry loading if image fails (might not be downloaded yet)
  // After 3 retries, switch to Scryfall fallback if available
  useEffect(() => {
    if (error && !useFallback) {
      // After 3 retries, try fallback URL if available
      if (retryCount >= 3 && fallbackUrl) {
        setUseFallback(true)
        setError(false)
        setRetryCount(0)
        return
      }
      
      if (retryCount < 10) {
        const delay = Math.min(1000 + retryCount * 500, 3000)
        const timer = setTimeout(() => {
          setError(false)
          setRetryCount(r => r + 1)
        }, delay)
        return () => clearTimeout(timer)
      }
    }
  }, [error, retryCount, fallbackUrl, useFallback])

  // Reset state ONLY when card URL actually changes (not on initial mount)
  const isFirstMount = React.useRef(true)
  const prevUrlRef = React.useRef(card.url)
  
  useEffect(() => {
    // Skip reset on first mount - let the image load naturally
    if (isFirstMount.current) {
      isFirstMount.current = false
      if (card.imageExists === false && fallbackUrl) {
        setUseFallback(true)
      }
      return
    }

    // Only reset if URL actually changed
    if (prevUrlRef.current !== card.url) {
      prevUrlRef.current = card.url
      setLoaded(false)
      setError(false)
      setRetryCount(0)
      setUseFallback(card.imageExists === false && !!fallbackUrl)
    }
  }, [card.url, card.imageExists, fallbackUrl])

  // State for back face loading errors
  const [backFaceError, setBackFaceError] = useState(false)
  const [backFaceRetryCount, setBackFaceRetryCount] = useState(0)

  // Determine which URL to use
  // If flipped and has back face, use the back face URL
  const frontUrl = useFallback && fallbackUrl ? fallbackUrl : card.url
  // For back face: try local first, fallback to Scryfall if error (after retries)
  const backUrl = backFaceError && backFaceFallback ? backFaceFallback : backFaceUrl
  const imageUrl = flipped && backUrl ? backUrl : frontUrl
  // Add retry param for back face too
  const finalUrl = error && !useFallback && !flipped
    ? `${imageUrl}?retry=${retryCount}`
    : (flipped && !backFaceError && backFaceRetryCount > 0)
      ? `${imageUrl}?retry=${backFaceRetryCount}`
      : imageUrl

  // Reset flipped state when card changes
  useEffect(() => {
    setFlipped(false)
    setBackFaceError(false)
    setBackFaceRetryCount(0)
  }, [card.url])

  // Reset loaded state when flipping to show loading spinner
  const prevFlippedRef = React.useRef(flipped)
  useEffect(() => {
    if (prevFlippedRef.current !== flipped) {
      prevFlippedRef.current = flipped
      setLoaded(false) // Show loading spinner while new face loads
    }
  }, [flipped])

  // Retry back face loading, then fallback to Scryfall
  useEffect(() => {
    if (flipped && !backFaceError && backFaceRetryCount > 0 && backFaceRetryCount < 3) {
      // Still retrying local back image
      const timer = setTimeout(() => {
        setBackFaceRetryCount(c => c + 1)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [flipped, backFaceError, backFaceRetryCount])


  return (
    <div
      style={{
        width,
        height,
        borderRadius: 8,
        background: '#333',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={() => setShowInfo(true)}
      onMouseLeave={() => setShowInfo(false)}>
      {/* Loading placeholder */}
      {!loaded && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(145deg, #2a2a2a, #333)',
          }}>
          <div
            style={{
              width: 24,
              height: 24,
              border: '2px solid #444',
              borderTopColor: '#888',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <div style={{color: '#666', fontSize: 10, marginTop: 8, textAlign: 'center', padding: '0 8px'}}>{card.name}</div>
        </div>
      )}
      <img
        src={finalUrl}
        alt={card.name}
        onLoad={() => {
          setLoaded(true)
          // If we loaded back face from Scryfall, download in background for next time
          if (flipped && backFaceError && backFaceFallback && card.url) {
            const backFilename = card.url.split('/').pop() + '_back'
            fetch('/saveCardArt', {
              method: 'POST',
              headers: {'Content-Type': 'application/json'},
              body: JSON.stringify({imgUrl: backFaceFallback, filename: backFilename}),
            })
              .then(r => r.json())
              .then(() => console.log(`[CardImage] Background saved back face: ${backFilename}`))
              .catch(() => {}) // Silently fail - not critical
          }
        }}
        onError={() => {
          if (flipped) {
            // Back face failed to load
            if (backFaceRetryCount < 2 && !backFaceError) {
              // Retry a couple times (image might still be downloading)
              setBackFaceRetryCount(c => c + 1)
            } else if (backFaceFallback && !backFaceError) {
              // Switch to Scryfall fallback
              console.log(`[CardImage] Back face failed, falling back to Scryfall for ${card.name}`)
              setBackFaceError(true)
              setBackFaceRetryCount(0)
            } else {
              // No fallback available or fallback also failed
              setError(true)
            }
          } else {
            setError(true)
          }
        }}
        style={{
          width,
          height,
          display: 'block',
          objectFit: 'cover',
          borderRadius: 8,
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />
      {/* Info overlay on hover */}
      {loaded && showInfo && card.setName && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.9))',
            padding: '20px 8px 8px',
            color: '#fff',
            fontSize: 11,
            textAlign: 'center',
          }}>
          <div style={{fontWeight: 500}}>{card.setName}</div>
          {card.artist && <div style={{color: '#aaa', fontSize: 10}}>Art by {card.artist}</div>}
        </div>
      )}
      {/* Flip button for double-faced cards - only show when we have Scryfall fallback ready */}
      {loaded && showFlip && hasTwoFaces && scryfallBackUrl && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            setFlipped(f => !f)
          }}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.6)',
            border: '1px solid rgba(255,255,255,0.3)',
            color: '#fff',
            fontSize: 14,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: showInfo ? 1 : 0.6,
            transition: 'opacity 0.2s, transform 0.3s',
            transform: flipped ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
          title={flipped ? 'Show front' : 'Show back'}>
          🔄
        </button>
      )}
    </div>
  )
}

const Previews = () => {
  const history = useHistory() // SPA navigation (no full-page reload) to the tier-list editor
  const [currentSet, setCurrentSet] = useState(null)
  const [allSetsList, setAllSetsList] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingSet, setLoadingSet] = useState(false)
  const [error, setError] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const setsScrollRef = useRef(null) // sets menu scroll container — its position is remembered client-side
  const restoredSetsScroll = useRef(false)
  const [menuFilter, setMenuFilter] = useState('') // Filter for sets menu
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600)

  // Remember/restore the sets menu scroll position (client-side) so reopening or refreshing lands
  // where you left off instead of jumping back to the top. Restores once, after the list is populated.
  useEffect(() => {
    if (restoredSetsScroll.current) return
    if (!setsScrollRef.current || allSetsList.length === 0) return
    try {
      let v = parseInt(localStorage.getItem('setsMenuScroll') || '', 10)
      if (v > 0) setsScrollRef.current.scrollTop = v
    } catch (e) {}
    restoredSetsScroll.current = true
  }, [allSetsList])

  // Search state — default open on desktop so a fresh page load auto-focuses search (type right away)
  const [searchOpen, setSearchOpen] = useState(window.innerWidth > 600)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  const [searching, setSearching] = useState(false)
  const [previousSet, setPreviousSet] = useState(null) // Store set before search

  // Sort/filter state
  const [sortBy, setSortBy] = useState('color-cmc') // default: color then cmc
  const [typeFilter, setTypeFilter] = useState('all') // card type filter

  // Layout state: 'stacked' (default on desktop) or 'grid' (default on mobile)
  const [layout, setLayout] = useState(() => (window.innerWidth <= 600 ? 'grid' : 'stacked'))
  const [expandedCard, setExpandedCard] = useState(null) // index of expanded card in stacked view

  // Mobile dropdown state
  const [activeDropdown, setActiveDropdown] = useState(null) // 'sort', 'type', or 'layout'

  // Board state
  const [boardOpen, setBoardOpen] = useState(false)

  // Theme state
  const [darkMode, setDarkMode] = useState(true)

  // Stats panel state (desktop)
  const [statsCard, setStatsCard] = useState(null) // card to show in stats panel
  const [statsCardSource, setStatsCardSource] = useState(null) // {stackId, deckCardId} if opened from deck
  const [showLargeCard, setShowLargeCard] = useState(true) // show large card image alongside stats
  const [showCardBack, setShowCardBack] = useState(false) // for double-faced cards, show back side
  const [showVariantsGrid, setShowVariantsGrid] = useState(() => {
    const saved = localStorage.getItem('showVariantsGrid')
    return saved === 'true'
  })

  // Save variants grid preference to localStorage
  useEffect(() => {
    localStorage.setItem('showVariantsGrid', showVariantsGrid.toString())
  }, [showVariantsGrid])

  // Fetch card_faces for statsCard if it's a transform card without card_faces data
  useEffect(() => {
    if (!statsCard) return
    const isTransform = statsCard.name?.includes(' // ')
    const hasCardFaces = statsCard.card_faces && statsCard.card_faces.length >= 2
    const scryfallId = statsCard.scryfallId || statsCard.id

    if (isTransform && !hasCardFaces && scryfallId) {
      console.log(`[Stats] Fetching card_faces for ${statsCard.name}...`)
      fetch(`https://api.scryfall.com/cards/${scryfallId}`)
        .then(r => r.json())
        .then(data => {
          if (data.card_faces && data.card_faces.length >= 2) {
            console.log(`[Stats] Got card_faces for ${statsCard.name}`)
            setStatsCard(prev => prev ? {...prev, card_faces: data.card_faces} : null)
          }
        })
        .catch(err => console.log(`[Stats] Failed to fetch card_faces:`, err))
    }
  }, [statsCard?.name, statsCard?.scryfallId, statsCard?.id])
  const [largeCardWidth, setLargeCardWidth] = useState(() => {
    const saved = localStorage.getItem('largeCardWidth')
    return saved ? parseInt(saved, 10) : 600
  })
  const [isDragging, setIsDragging] = useState(false)

  // User authentication state
  const [currentUser, setCurrentUser] = useState(null) // {username, displayName}
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [loginMode, setLoginMode] = useState('login') // 'login' or 'register'
  const [loginUsername, setLoginUsername] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)

  // User profile & deck builder state
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [deckRearrangeMode, setDeckRearrangeMode] = useState(false)
  const [draggingDeckId, setDraggingDeckId] = useState(null)
  const [dragOverDeckId, setDragOverDeckId] = useState(null)
  const [dragOverDeckPosition, setDragOverDeckPosition] = useState('before')
  const [decks, setDecks] = useState(() => {
    const saved = localStorage.getItem('mtgDecks')
    return saved ? JSON.parse(saved) : []
  })
  const [activeDeck, setActiveDeck] = useState(null) // currently editing deck
  const [deckSearchQuery, setDeckSearchQuery] = useState('')
  const [deckSearchResults, setDeckSearchResults] = useState([])
  const [deckSearching, setDeckSearching] = useState(false)
  const [draggingCard, setDraggingCard] = useState(null) // card being dragged in deck builder
  const didDragRef = useRef(false) // Track if drag occurred (to prevent click after drag)
  const [dragOverStack, setDragOverStack] = useState(null) // stack being dragged over
  const [dragOverCommander, setDragOverCommander] = useState(false) // dragging over commander zone

  useEffect(() => {
    if (!userMenuOpen) {
      setDeckRearrangeMode(false)
      setDraggingDeckId(null)
      setDragOverDeckId(null)
      setDragOverDeckPosition('before')
      return
    }

    const handleDeckRearrangeShortcut = e => {
      const targetTag = e.target?.tagName?.toLowerCase()
      if (targetTag === 'input' || targetTag === 'textarea' || e.target?.isContentEditable) return

      if (e.key?.toLowerCase() === 'h') {
        e.preventDefault()
        setDeckRearrangeMode(prev => !prev)
        setDraggingDeckId(null)
        setDragOverDeckId(null)
        setDragOverDeckPosition('before')
      } else if (e.key === 'Escape') {
        setDeckRearrangeMode(false)
        setDraggingDeckId(null)
        setDragOverDeckId(null)
        setDragOverDeckPosition('before')
      }
    }

    window.addEventListener('keydown', handleDeckRearrangeShortcut)
    return () => window.removeEventListener('keydown', handleDeckRearrangeShortcut)
  }, [userMenuOpen])

  // Middle-click panning for stacks container
  const stacksContainerRef = useRef(null)
  const [isPanning, setIsPanning] = useState(false)
  const [panStart, setPanStart] = useState({x: 0, scrollLeft: 0})

  // Expanded card state (needed before masonry calc)
  const [expandedDeckCard, setExpandedDeckCard] = useState(null) // {stackId, cardIndex} for expanded card in deck view

  // Deck card size scale (adjustable via +/- buttons)
  const [deckCardScale, setDeckCardScale] = useState(() => {
    const saved = localStorage.getItem('deckCardScale')
    return saved ? parseFloat(saved) : 1
  })

  // Masonry layout state
  const stackRefs = useRef({})
  const [masonryPositions, setMasonryPositions] = useState([])
  const [masonryHeight, setMasonryHeight] = useState(0)
  const BASE_STACK_WIDTH = isMobile ? 150 : 190
  const STACK_WIDTH = Math.round(BASE_STACK_WIDTH * deckCardScale)
  const STACK_GAP = 8

  // Calculate masonry positions when stacks change
  useEffect(() => {
    if (!activeDeck?.stacks?.length || !stacksContainerRef.current) return

    // Wait for DOM to update
    const timer = setTimeout(() => {
      const containerWidth = stacksContainerRef.current?.clientWidth - 32 // account for padding
      if (!containerWidth) return

      const numColumns = Math.max(1, Math.floor((containerWidth + STACK_GAP) / (STACK_WIDTH + STACK_GAP)))
      const columnHeights = new Array(numColumns).fill(0)
      const positions = []

      activeDeck.stacks.forEach((stack, index) => {
        // Find shortest column
        const shortestCol = columnHeights.indexOf(Math.min(...columnHeights))
        const x = shortestCol * (STACK_WIDTH + STACK_GAP)
        const y = columnHeights[shortestCol]

        // Get actual height of this stack
        const stackEl = stackRefs.current[stack.id]
        const stackHeight = stackEl?.offsetHeight || 400

        positions.push({ x, y, width: STACK_WIDTH })
        columnHeights[shortestCol] = y + stackHeight + STACK_GAP
      })

      setMasonryPositions(positions)
      setMasonryHeight(Math.max(...columnHeights) + 500) // Extra space for card expansion
    }, 50)

    return () => clearTimeout(timer)
  }, [activeDeck?.stacks, activeDeck?.stacks?.map(s => s.cards.length).join(','), isMobile, STACK_WIDTH, STACK_GAP])

  // Recalculate masonry on window resize
  useEffect(() => {
    const handleResize = () => {
      if (activeDeck?.stacks?.length) {
        // Trigger recalc by updating a dependency
        setMasonryPositions([])
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [activeDeck?.stacks?.length])

  // Stack reordering state
  const [draggingStack, setDraggingStack] = useState(null)
  const [editingStackId, setEditingStackId] = useState(null) // stack name being edited
  const [stackMenuOpen, setStackMenuOpen] = useState(null) // stack id with menu open
  const [dragOverStackPosition, setDragOverStackPosition] = useState(null)

  // Import deck state
  const [showImportPanel, setShowImportPanel] = useState(false)
  const [importText, setImportText] = useState('')
  const [importing, setImporting] = useState(false)
  const [importProgress, setImportProgress] = useState({current: 0, total: 0, status: ''})

  // Export toast state
  const [showExportToast, setShowExportToast] = useState(false)

  // AI question state
  const [showAiQuestion, setShowAiQuestion] = useState(false)
  const [aiQuestion, setAiQuestion] = useState('')
  const [aiAsking, setAiAsking] = useState(false)
  const [aiResponse, setAiResponse] = useState(null)
  const [aiReasoningEffort, setAiReasoningEffort] = useState('medium') // low, medium, high
  const [aiCards, setAiCards] = useState([]) // resolved cards with images: {name, why, tags, image, fromLocal}
  const [aiCardsLoading, setAiCardsLoading] = useState(false)
  const [hoveredAiCard, setHoveredAiCard] = useState(null) // card name being hovered
  const [hasApiKey, setHasApiKey] = useState(null) // null = checking, true/false = result
  const [aiPanelApiKey, setAiPanelApiKey] = useState('') // API key input in AI panel
  const [aiPanelSaving, setAiPanelSaving] = useState(false)
  const [aiPanelSaved, setAiPanelSaved] = useState(false)
  const aiQuestionRef = useRef(null)

  // Check for API key when AI question panel opens
  useEffect(() => {
    if (showAiQuestion && hasApiKey === null) {
      Axios.get('/api/get-api-key').then(res => {
        setHasApiKey(res.data.hasKey || false)
      }).catch(() => setHasApiKey(false))
    }
  }, [showAiQuestion])

  // Save API key from AI panel
  const saveAiPanelApiKey = async () => {
    if (!aiPanelApiKey.trim() || aiPanelSaving) return
    setAiPanelSaving(true)
    try {
      await Axios.post('/api/save-api-key', { apiKey: aiPanelApiKey })
      setAiPanelSaved(true)
      setHasApiKey(true)
      setTimeout(() => setAiPanelSaved(false), 2000)
    } catch (err) {
      console.error('Failed to save API key:', err)
    } finally {
      setAiPanelSaving(false)
    }
  }

  // Login/Register handler
  const handleAuth = async () => {
    if (!loginUsername.trim() || !loginPassword.trim()) {
      setLoginError('Username and password required')
      return
    }

    setLoginLoading(true)
    setLoginError('')

    try {
      const endpoint = loginMode === 'register' ? '/api/auth/register' : '/api/auth/login'
      const res = await Axios.post(endpoint, {
        username: loginUsername,
        password: loginPassword
      })

      if (res.data.success) {
        // Load user's decks FIRST
        const decksRes = await Axios.get('/api/decks')
        const serverDecks = decksRes.data.decks || []
        // Clear localStorage since we're using MongoDB now
        localStorage.removeItem('mtgDecks')
        // Set decks before currentUser to avoid sync race
        setDecks(serverDecks)
        setCurrentUser(res.data.user)
        setShowLoginModal(false)
        setLoginUsername('')
        setLoginPassword('')
        // Reset API key check for AI panel
        setHasApiKey(null)
      }
    } catch (err) {
      setLoginError(err.response?.data?.error || 'Authentication failed')
    } finally {
      setLoginLoading(false)
    }
  }

  // Logout handler
  const handleLogout = async () => {
    try {
      await Axios.post('/api/auth/logout')
      setCurrentUser(null)
      setHasApiKey(null)
      // Clear decks and close deck editor
      setActiveDeck(null)
      setDecks([])
      localStorage.removeItem('mtgDecks')
      localStorage.removeItem('activeDeckId')
    } catch (err) {
      console.error('[Auth] Logout error:', err)
    }
  }

  // Resolve AI card suggestions to get images
  const resolveAiCards = async (cards) => {
    if (!cards || cards.length === 0) return

    setAiCardsLoading(true)
    setAiCards([])
    const resolved = []
    const backendUrl = getBackendUrl()

    for (const card of cards) {
      try {
        // Use the card-lookup endpoint which checks MongoDB first, then Scryfall
        const res = await fetch(`${backendUrl}/api/card-lookup?name=${encodeURIComponent(card.name)}`)
        const data = await res.json()

        if (data.success && data.card) {
          const source = data.source === 'mongodb' ? 'MONGODB' : 'SCRYFALL'
          console.log(`[AI Cards] Found "${card.name}" via ${source}`)
          resolved.push({
            ...card,
            name: data.card.name, // use actual name from lookup
            image: data.card.image_uri,
            scryfallData: data.card,
            fromLocal: data.source === 'mongodb'
          })
        } else {
          console.log(`[AI Cards] Could not find "${card.name}"`)
          resolved.push({
            ...card,
            image: null,
            fromLocal: false
          })
        }

        // Update as we go for better UX
        setAiCards([...resolved])

        // Small delay between requests
        await new Promise(r => setTimeout(r, 50))
      } catch (err) {
        console.error(`[AI Cards] Error resolving "${card.name}":`, err)
        resolved.push({
          ...card,
          image: null,
          fromLocal: false
        })
        setAiCards([...resolved])
      }
    }

    setAiCardsLoading(false)
  }

  // Focus AI question textarea when opened (only if API key exists)
  useEffect(() => {
    if (showAiQuestion && hasApiKey && aiQuestionRef.current) {
      aiQuestionRef.current.focus()
    }
  }, [showAiQuestion, hasApiKey])

  // Settings state
  const [showSettings, setShowSettings] = useState(false)
  const [userApiKey, setUserApiKey] = useState('')
  const [apiKeyPreview, setApiKeyPreview] = useState('') // masked preview like sk-abc...xyz
  const [savingApiKey, setSavingApiKey] = useState(false)
  const [apiKeySaved, setApiKeySaved] = useState(false)

  // Load API key preview when settings opens
  useEffect(() => {
    if (showSettings) {
      Axios.get('/api/get-api-key').then(res => {
        if (res.data.hasKey && res.data.keyPreview) {
          setApiKeyPreview(res.data.keyPreview)
          setUserApiKey(res.data.keyPreview) // Show preview in input
        }
      }).catch(() => {})
    }
  }, [showSettings])

  // Optimize deck prices state
  const [optimizing, setOptimizing] = useState(false)
  const [optimizeProgress, setOptimizeProgress] = useState({current: 0, total: 0, saved: 0})

  // Stack card sort order
  const [stackSortOrder, setStackSortOrder] = useState('custom') // 'custom', 'cmc', 'price'

  // Deck name editing state
  const [editingDeckName, setEditingDeckName] = useState(false)

  // Price refresh state
  const [refreshingPrice, setRefreshingPrice] = useState(false)
  const [priceHistory, setPriceHistory] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [backfillingHistory, setBackfillingHistory] = useState(false)
  const [chartHoverPoint, setChartHoverPoint] = useState(null)
  const [cardVariants, setCardVariants] = useState([])
  const [allVariants, setAllVariants] = useState([]) // Full list from Scryfall
  const [variantsLimit, setVariantsLimit] = useState(15)
  const [loadingVariants, setLoadingVariants] = useState(false)
  const [loadingMoreVariants, setLoadingMoreVariants] = useState(false)
  const [hoveredVariantImage, setHoveredVariantImage] = useState(null)
  const [lockedVariantImage, setLockedVariantImage] = useState(null)

  // Note: Vertical scrolling is now handled by the main deck builder container
  // Horizontal scrolling of stacks is done via middle-mouse-button drag

  // Save large card width to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('largeCardWidth', largeCardWidth.toString())
  }, [largeCardWidth])

  // Save active deck ID to localStorage when it changes
  // Use a ref to track if we've ever had an active deck (to avoid clearing on initial load)
  const hadActiveDeckRef = useRef(false)
  useEffect(() => {
    console.log(`[ActiveDeck] Changed to: ${activeDeck?.name || 'null'}, id: ${activeDeck?.id || 'null'}`)
    if (activeDeck) {
      hadActiveDeckRef.current = true
      console.log(`[ActiveDeck] Saving to localStorage: ${activeDeck.id}`)
      localStorage.setItem('activeDeckId', activeDeck.id)
      // Verify it was saved
      const saved = localStorage.getItem('activeDeckId')
      console.log(`[ActiveDeck] Verified localStorage value: ${saved}`)
    } else if (hadActiveDeckRef.current) {
      // Only remove if we previously had a deck (user explicitly closed it)
      console.log('[ActiveDeck] Removing from localStorage (user closed deck)')
      localStorage.removeItem('activeDeckId')
    } else {
      console.log('[ActiveDeck] Initial null state, not clearing localStorage')
    }
  }, [activeDeck])

  // Check login status on mount and load user's decks
  const [authChecked, setAuthChecked] = useState(false)
  useEffect(() => {
    const checkAuth = async () => {
      console.log('[Auth] Starting auth check...')
      try {
        const res = await Axios.get('/api/auth/me')
        console.log('[Auth] /api/auth/me response:', res.data)
        if (res.data.loggedIn) {
          // Load user's decks from server FIRST
          const decksRes = await Axios.get('/api/decks')
          const serverDecks = decksRes.data.decks || []
          // Clear localStorage decks since we're using MongoDB
          localStorage.removeItem('mtgDecks')
          // Set decks before currentUser to avoid sync race condition
          setDecks(serverDecks)
          setCurrentUser(res.data.user)
          console.log(`[Auth] Loaded ${serverDecks.length} decks for ${res.data.user.username}`)
          console.log(`[Auth] Deck IDs:`, serverDecks.map(d => d.id))
          // Restore active deck if one was open
          const savedDeckId = localStorage.getItem('activeDeckId')
          console.log(`[Auth] savedDeckId from localStorage:`, savedDeckId)
          if (savedDeckId) {
            const deckToRestore = serverDecks.find(d => d.id === savedDeckId)
            console.log(`[Auth] deckToRestore:`, deckToRestore ? deckToRestore.name : 'NOT FOUND')
            if (deckToRestore) {
              console.log(`[Auth] Restoring deck: ${deckToRestore.name}`)
              setActiveDeck(deckToRestore)
            } else {
              console.log(`[Auth] Could not find deck with id ${savedDeckId} in server decks`)
            }
          } else {
            console.log('[Auth] No savedDeckId in localStorage')
          }
        }
      } catch (err) {
        console.log('[Auth] Not logged in or error:', err.message)
      }
      console.log('[Auth] Setting authChecked = true')
      setAuthChecked(true)
    }
    checkAuth()
  }, [])

  // Save decks to backend when logged in (localStorage no longer used when logged in)
  useEffect(() => {
    // Don't sync until we've checked auth status (prevents localStorage overwriting MongoDB)
    if (!authChecked) return

    if (currentUser) {
      // When logged in, sync to MongoDB
      Axios.post('/api/decks', { decks }).catch(err => {
        console.error('[Decks] Failed to sync:', err.message)
      })
    } else {
      // When not logged in, save to localStorage
      localStorage.setItem('mtgDecks', JSON.stringify(decks))
    }
  }, [decks, currentUser, authChecked])

  // Refresh prices and fetch history when stats panel opens
  useEffect(() => {
    const cardSetCode = statsCard?.setCode || statsCard?.set
    if (!statsCard || !cardSetCode || !statsCard.collector_number) return

    // Reset hover/locked state when card changes
    setHoveredVariantImage(null)
    setLockedVariantImage(null)

    async function refreshPrices() {
      try {
        const backendUrl = getBackendUrl()
        const res = await Axios.get(`${backendUrl}/previews/card-prices/${cardSetCode}/${statsCard.collector_number}`)
        if (res.data.prices) {
          // Update statsCard with fresh prices
          setStatsCard(prev => (prev ? {...prev, prices: res.data.prices} : null))
          console.log(`[Stats] Prices refreshed for ${statsCard.name}: $${res.data.prices.usd || 'N/A'}`)
        }
      } catch (err) {
        console.log(`[Stats] Failed to refresh prices:`, err.message)
      }
    }

    async function fetchPriceHistory() {
      setLoadingHistory(true)
      try {
        const backendUrl = getBackendUrl()
        const res = await Axios.get(`${backendUrl}/previews/card-price-history/${cardSetCode}/${statsCard.collector_number}`)
        const history = res.data.history || []
        setPriceHistory(history)
        console.log(`[Stats] Price history loaded: ${history.length} data points`)

        // If no history or only 1 point, auto-trigger backfill to get at least today's price
        if (history.length < 2) {
          console.log(`[Stats] Auto-triggering backfill for ${statsCard.name}...`)
          try {
            const backfillRes = await Axios.get(
              `${backendUrl}/previews/backfill-price-history/${cardSetCode}/${statsCard.collector_number}`,
            )
            if (backfillRes.data.history) {
              setPriceHistory(backfillRes.data.history)
              console.log(`[Stats] Auto-backfill complete: ${backfillRes.data.history.length} data points`)
            }
          } catch (backfillErr) {
            console.log(`[Stats] Auto-backfill failed:`, backfillErr.message)
          }
        }
      } catch (err) {
        console.log(`[Stats] Failed to fetch price history:`, err.message)
        setPriceHistory([])

        // Even if history fetch fails, try backfill to get current price
        try {
          const backendUrl = getBackendUrl()
          const backfillRes = await Axios.get(
            `${backendUrl}/previews/backfill-price-history/${cardSetCode}/${statsCard.collector_number}`,
          )
          if (backfillRes.data.history) {
            setPriceHistory(backfillRes.data.history)
            console.log(`[Stats] Fallback backfill complete: ${backfillRes.data.history.length} data points`)
          }
        } catch (backfillErr) {
          console.log(`[Stats] Fallback backfill also failed:`, backfillErr.message)
        }
      } finally {
        setLoadingHistory(false)
      }
    }

    async function fetchVariants() {
      if (!statsCard.oracle_id && !statsCard.name) return
      setLoadingVariants(true)
      setCardVariants([])
      setAllVariants([])
      setVariantsLimit(15) // Reset limit when opening new card
      try {
        // Use oracle_id if available (most accurate), otherwise search by name
        const searchParam = statsCard.oracle_id ? `oracle_id:${statsCard.oracle_id}` : `!"${statsCard.name}"`
        const res = await Axios.get(
          `https://api.scryfall.com/cards/search?q=${encodeURIComponent(searchParam)}&unique=prints&order=released&dir=desc`,
          {timeout: 10000},
        )
        if (res.data.data) {
          // Filter to only cards with prices (keep full list)
          const backendUrl = getBackendUrl()
          const allVariantsData = res.data.data
            .filter(c => c.prices?.usd || c.prices?.usd_foil || c.prices?.tix)
            .map(c => ({
              id: c.id,
              set_name: c.set_name,
              set: c.set,
              collector_number: c.collector_number,
              finishes: c.finishes || [],
              frame_effects: c.frame_effects || [],
              promo_types: c.promo_types || [],
              usd: c.prices?.usd,
              usd_foil: c.prices?.usd_foil,
              tix: c.prices?.tix,
              // Scryfall URL as fallback
              scryfall_uri: c.image_uris?.normal || c.image_uris?.large || c.card_faces?.[0]?.image_uris?.normal,
              // Our cached URL (will check if exists)
              cache_url: `${backendUrl}/card-variant/${c.set}/${c.collector_number}`,
              // Will be set after checking cache
              image_uri: null,
              is_cached: false,
            }))

          // Store full list for "load more"
          setAllVariants(allVariantsData)

          // Check cache only for first batch (15)
          const firstBatch = allVariantsData.slice(0, 15)
          const variantsWithCache = await Promise.all(
            firstBatch.map(async v => {
              try {
                await Axios.head(v.cache_url, {timeout: 1000})
                return {...v, image_uri: v.cache_url, is_cached: true}
              } catch (e) {
                return {...v, image_uri: v.scryfall_uri, is_cached: false}
              }
            }),
          )
          setCardVariants(variantsWithCache)
          console.log(`[Stats] Found ${allVariantsData.length} printings for ${statsCard.name} (showing 15)`)
        }
      } catch (err) {
        console.log(`[Stats] Failed to fetch variants:`, err.message)
      } finally {
        setLoadingVariants(false)
      }
    }

    refreshPrices()
    fetchPriceHistory()
    fetchVariants()
  }, [statsCard?.setCode, statsCard?.set, statsCard?.collector_number, statsCard?.oracle_id, statsCard?.name])

  // Load more variants (checks cache for new batch)
  const loadMoreVariants = async () => {
    if (loadingMoreVariants || cardVariants.length >= allVariants.length) return
    setLoadingMoreVariants(true)
    try {
      const newLimit = variantsLimit + 20
      const newBatch = allVariants.slice(variantsLimit, newLimit)

      // Check cache for new batch
      const batchWithCache = await Promise.all(
        newBatch.map(async v => {
          try {
            await Axios.head(v.cache_url, {timeout: 1000})
            return {...v, image_uri: v.cache_url, is_cached: true}
          } catch (e) {
            return {...v, image_uri: v.scryfall_uri, is_cached: false}
          }
        }),
      )

      setCardVariants(prev => [...prev, ...batchWithCache])
      setVariantsLimit(newLimit)
      console.log(
        `[Stats] Loaded more variants: now showing ${Math.min(newLimit, allVariants.length)} of ${allVariants.length}`,
      )
    } catch (err) {
      console.log(`[Stats] Failed to load more variants:`, err.message)
    } finally {
      setLoadingMoreVariants(false)
    }
  }

  // Manual price refresh (with rate limiting on server)
  const manualRefreshPrice = async () => {
    const cardSetCode = statsCard?.setCode || statsCard?.set
    if (!statsCard || !cardSetCode || !statsCard.collector_number || refreshingPrice) return

    setRefreshingPrice(true)
    try {
      const backendUrl = getBackendUrl()
      // Add a cache-busting param to force fresh request
      const res = await Axios.get(
        `${backendUrl}/previews/card-prices/${cardSetCode}/${statsCard.collector_number}?refresh=true&t=${Date.now()}`,
      )
      if (res.data.prices) {
        setStatsCard(prev => (prev ? {...prev, prices: res.data.prices} : null))
        console.log(
          `[Stats] Manual refresh for ${statsCard.name}: $${res.data.prices.usd || 'N/A'} (cached: ${res.data.cached})`,
        )

        // Also refresh history after manual price update
        if (!res.data.cached) {
          const histRes = await Axios.get(
            `${backendUrl}/previews/card-price-history/${cardSetCode}/${statsCard.collector_number}`,
          )
          setPriceHistory(histRes.data.history || [])
        }
      }
    } catch (err) {
      console.log(`[Stats] Failed to manually refresh prices:`, err.message)
    } finally {
      setRefreshingPrice(false)
    }
  }

  // Backfill historical price data
  const backfillPriceHistory = async () => {
    const cardSetCode = statsCard?.setCode || statsCard?.set
    if (!statsCard || !cardSetCode || !statsCard.collector_number || backfillingHistory) return

    setBackfillingHistory(true)
    try {
      const backendUrl = getBackendUrl()
      const res = await Axios.get(
        `${backendUrl}/previews/backfill-price-history/${cardSetCode}/${statsCard.collector_number}`,
      )
      if (res.data.history) {
        setPriceHistory(res.data.history)
        console.log(
          `[Stats] Backfilled ${res.data.backfilled || 0} price points for ${statsCard.name} from ${res.data.source}`,
        )
      }
    } catch (err) {
      console.log(`[Stats] Failed to backfill price history:`, err.response?.data?.message || err.message)
    } finally {
      setBackfillingHistory(false)
    }
  }

  // Handle drag resize for large card panel
  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = e => {
      const newWidth = window.innerWidth - e.clientX - 300 // 300 is stats panel width
      const clampedWidth = Math.max(400, Math.min(900, newWidth)) // min 400, max 900
      setLargeCardWidth(clampedWidth)
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging])

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Fetch all set names from Scryfall on mount
  useEffect(() => {
    async function fetchSetList() {
      const startTime = Date.now()
      console.log(`[Sets] 📡 Fetching set list...`)
      try {
        const backendUrl = getBackendUrl()
        const res = await Axios.get(`${backendUrl}/sets/list`)
        console.log(
          `[Sets] ⏱️ Response received in ${Date.now() - startTime}ms, size: ${JSON.stringify(res.data).length} bytes`,
        )
        // Filter to main set types
        const validTypes = [
          'core',
          'expansion',
          'masters',
          'draft_innovation',
          'commander',
          'starter',
          'promo',
          'box',
          'archenemy',
          'planechase',
          'duel_deck',
          'premium_deck',
          'from_the_vault',
          'spellbook',
          'masterpiece',
          'arsenal',
          'treasure_chest',
        ]
        const filtered = res.data.filter(s => validTypes.includes(s.set_type) || !s.set_type)
        setAllSetsList(filtered)
        console.log(`[Previews] Got ${filtered.length} sets from list`)
      } catch (err) {
        console.error(`[Previews] Error fetching set list:`, err)
      }
    }
    fetchSetList()
  }, [])

  // Load initial/latest set on mount (or last viewed set from localStorage)
  // Wait for authChecked so we know if user is logged in and deck was restored
  const initialLoadDoneRef = useRef(false)
  useEffect(() => {
    console.log(`[FetchInitialSet] Effect running. authChecked=${authChecked}, activeDeck=${activeDeck?.name || 'null'}, initialLoadDone=${initialLoadDoneRef.current}`)
    if (!authChecked) {
      console.log('[FetchInitialSet] Waiting for auth check...')
      return // Wait for auth check to complete
    }
    if (initialLoadDoneRef.current) {
      console.log('[FetchInitialSet] Already done, skipping')
      return // Only run once
    }
    initialLoadDoneRef.current = true
    console.log('[FetchInitialSet] Running fetchInitialSet...')

    async function fetchInitialSet() {
      // If activeDeck was already restored by checkAuth, don't load a set
      console.log(`[FetchInitialSet] Inside async. activeDeck=${activeDeck?.name || 'null'}`)
      if (activeDeck) {
        console.log(`[FetchInitialSet] Deck already active (${activeDeck.name}), skipping set load`)
        setLoading(false)
        return
      }

      console.log('[FetchInitialSet] No active deck, loading set...')
      setLoading(true)
      setError(null)

      const backendUrl = getBackendUrl()

      // Check what the last view type was (deck or set)
      const lastViewType = localStorage.getItem('lastViewType')

      // If last view was a deck and not logged in, try to restore from localStorage
      if (lastViewType === 'deck' && !currentUser) {
        const savedDeckId = localStorage.getItem('lastViewedDeck')
        if (savedDeckId) {
          const savedDecks = localStorage.getItem('mtgDecks')
          if (savedDecks) {
            try {
              const parsedDecks = JSON.parse(savedDecks)
              const deckToOpen = parsedDecks.find(d => d.id === savedDeckId)
              if (deckToOpen) {
                console.log(`[Previews] Restoring last viewed deck: ${deckToOpen.name}`)
                setActiveDeck(deckToOpen)
                setStackSortOrder(deckToOpen.sortOrder || 'custom')
                setLoading(false)
                return
              }
            } catch (err) {
              console.log(`[Previews] Failed to restore last deck, continuing to set load`)
            }
          }
        }
      }

      // Check if we have a last viewed set saved
      const savedSet = localStorage.getItem('lastViewedSet')
      if (savedSet) {
        try {
          const {setCode, setName} = JSON.parse(savedSet)
          console.log(`[Previews] Loading last viewed set: ${setName} (${setCode})`)

          const res = await Axios.get(`${backendUrl}/previews/set/${setCode}`)
          if (res.data.cards && res.data.cards.length > 0) {
            setCurrentSet(res.data)
            console.log(`[Previews] Restored last viewed set: ${setName} (${res.data.cards.length} cards)`)
            setLoading(false)
            return
          }
        } catch (err) {
          console.log(`[Previews] Last viewed set not available, falling back to latest`)
          // Fall through to load latest
        }
      }

      // Fallback: load latest set
      try {
        const res = await Axios.get(`${backendUrl}/previews/latest`)

        if (res.data.sets && res.data.sets.length > 0) {
          // Show the first (newest) set by default
          setCurrentSet(res.data.sets[0])
          console.log(`[Previews] Loaded latest set: ${res.data.sets[0].setName}`)
        }
      } catch (err) {
        console.error(`[Previews] Error:`, err)
        if (err.response?.status === 404) {
          setError('No sets available yet. The server is syncing cards from Scryfall...')
        } else {
          setError('Failed to load cards. Please try again.')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchInitialSet()
  }, [authChecked])

  // Load a specific set (on-demand)
  const loadSet = async (setCode, setName) => {
    setMenuOpen(false)
    setLoadingSet(true)
    setCurrentSet(null) // Clear current cards immediately
    setError(null)
    setExpandedCard(null) // Reset expanded card
    setStatsCard(null) // Close stats panel

    // Save as last viewed set and mark last view as set
    localStorage.setItem('lastViewedSet', JSON.stringify({setCode, setName}))
    localStorage.setItem('lastViewType', 'set')

    const backendUrl = getBackendUrl()

    try {
      // First check if we already have it cached via direct set endpoint
      try {
        const res = await Axios.get(`${backendUrl}/previews/set/${setCode}`)
        if (res.data.cards && res.data.cards.length > 0) {
          // Note: if res.data.downloading is true, some images may be missing
          // User can resume download via Admin panel - we don't auto-trigger here
          // to avoid expensive filesystem checks on every page view
          if (res.data.downloading) {
            console.log(`[Previews] Set ${setCode} has incomplete download - resume via Admin panel`)
          }
          setCurrentSet(res.data)
          console.log(`[Previews] Loaded cached set: ${setName} (${res.data.cards.length} cards)`)
          setLoadingSet(false)
          return
        }
        if (res.data.noCards) {
          setError(`${setName} has no cards available yet (not released).`)
          setLoadingSet(false)
          return
        }
      } catch (e) {
        // 404 means not cached, continue to download
        if (e.response?.status !== 404) {
          console.log(`[Previews] Error checking cache:`, e.message)
        }
      }

      // Not cached - trigger download
      console.log(`[Previews] Set ${setCode} not cached, triggering download...`)

      const loadRes = await Axios.post(`${backendUrl}/previews/load-set`, {
        setCode,
        setName,
      })

      console.log(`[Previews] Server response:`, loadRes.data)

      // If already cached, load it now
      if (loadRes.data.status === 'cached') {
        const freshRes = await Axios.get(`${backendUrl}/previews/set/${setCode}`)
        if (freshRes.data.cards) {
          setCurrentSet(freshRes.data)
          setLoadingSet(false)
          return
        }
      }

      // If set has no cards (not released yet), show error
      if (loadRes.data.status === 'no_cards') {
        setError(`${setName} has no cards available yet (not released).`)
        setLoadingSet(false)
        return
      }

      // Poll to get the card list (metadata saved after Scryfall fetch completes)
      console.log(`[Previews] Waiting for card list...`)

      // Give backend time to fetch from Scryfall and save metadata
      // Large sets can take 5-10+ seconds to fetch all pages
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Try to get card list - poll for up to 2 minutes (large sets take time)
      let attempts = 0
      const maxAttempts = 120 // 2 minutes at 1 second intervals

      while (attempts < maxAttempts) {
        try {
          const checkRes = await Axios.get(`${backendUrl}/previews/set/${setCode}`)

          if (checkRes.data.cards && checkRes.data.cards.length > 0) {
            setCurrentSet(checkRes.data)
            setLoadingSet(false)
            console.log(`[Previews] Got ${checkRes.data.cards.length} cards for ${setCode}, images will stream in`)
            return
          }
        } catch (e) {
          // 404 is expected while backend is still fetching from Scryfall
          // Only log every 10 attempts to reduce console noise
          if (attempts % 10 === 0) {
            console.log(`[Previews] Still waiting for ${setCode}... (attempt ${attempts + 1})`)
          }
        }

        attempts++
        await new Promise(resolve => setTimeout(resolve, 1000))
      }

      setError(`Could not load ${setName}. Please try again.`)
      setLoadingSet(false)
    } catch (err) {
      console.error(`[Previews] Error loading set ${setCode}:`, err)
      setError(`Failed to load ${setName}. Please try again.`)
      setLoadingSet(false)
    }
  }

  // Search state for pagination
  const [searchHasMore, setSearchHasMore] = useState(false)
  const [searchNextPage, setSearchNextPage] = useState(null)
  const [loadingMore, setLoadingMore] = useState(false)

  // Search for a card - get all unique arts
  const handleSearch = async () => {
    if (!searchQuery.trim()) return

    setSearching(true)
    setError(null)
    setSearchHasMore(false)
    setSearchNextPage(null)
    const backendUrl = getBackendUrl()
    const query = searchQuery.trim()


    try {
      // Save current set so we can restore it
      if (!searchResults && currentSet) {
        setPreviousSet(currentSet)
      }

      // First, search our local DB via backend endpoint
      const localRes = await Axios.get(`${backendUrl}/previews/search?q=${encodeURIComponent(query)}`)

      if (localRes.data.results && localRes.data.results.length > 0) {
        setSearchResults(localRes.data.results)
        setSearching(false)
        return
      }

      // Not found locally - query Scryfall for all unique arts

      try {
        // Use search API with unique:art to get all different artworks
        const scryfallRes = await Axios.get(
          `https://api.scryfall.com/cards/search?q=${encodeURIComponent(query)}&unique=art&order=released&dir=desc`,
        )

        const cards = scryfallRes.data.data || []
        const hasMore = scryfallRes.data.has_more || false
        const nextPage = scryfallRes.data.next_page || null


        if (cards.length === 0) {
          setError(`Card "${query}" not found.`)
          setSearching(false)
          return
        }

        // Take first 10 results
        const cardsToShow = cards.slice(0, 10)
        const results = []

        // Download each card to our DB
        for (const card of cardsToShow) {
          const filename = `${card.name.replace(/[^a-zA-Z0-9]/g, '_')}_${card.set}`
          const imgUrl = card.image_uris?.normal || card.card_faces?.[0]?.image_uris?.normal

          if (imgUrl) {
            const localUrl = `${backendUrl}/card/${filename}`
            
            // Save card to backend (fire and forget, don't wait)
            Axios.post(`${backendUrl}/cards/save`, {
              imgUrl,
              filename,
              setName: card.set_name,
              setCode: card.set,
            })
              .catch(() => {}) // Silent fail for caching

            results.push({
              name: card.name,
              url: localUrl,
              scryfallUrl: imgUrl, // Keep original URL for debugging
              setCode: card.set,
              setName: card.set_name,
              collector_number: card.collector_number,
              colors: card.colors,
              cmc: card.cmc,
              type_line: card.type_line,
              mana_cost: card.mana_cost,
              rarity: card.rarity,
              artist: card.artist,
              source: 'scryfall',
            })
          }
        }

        setSearchResults(results)

        // Store pagination info if there are more than 10 total
        if (cards.length > 10 || hasMore) {
          setSearchHasMore(true)
          // Store remaining cards from this page + next page URL
          setSearchNextPage({
            remainingCards: cards.slice(10),
            nextPageUrl: nextPage,
          })
        }
      } catch (scryfallErr) {
        setError(`Card "${query}" not found.`)
      }
    } catch (err) {
      console.error(`[Search] Error:`, err)
      setError('Search failed. Please try again.')
    } finally {
      setSearching(false)
    }
  }

  // Load more search results
  const loadMoreResults = async () => {
    if (!searchNextPage || loadingMore) return

    setLoadingMore(true)
    const backendUrl = getBackendUrl()

    try {
      let cardsToProcess = []

      // First use remaining cards from previous page
      if (searchNextPage.remainingCards && searchNextPage.remainingCards.length > 0) {
        cardsToProcess = searchNextPage.remainingCards.slice(0, 10)
        const stillRemaining = searchNextPage.remainingCards.slice(10)

        if (stillRemaining.length > 0) {
          setSearchNextPage({
            remainingCards: stillRemaining,
            nextPageUrl: searchNextPage.nextPageUrl,
          })
        } else if (searchNextPage.nextPageUrl) {
          // Fetch next page
          const nextRes = await Axios.get(searchNextPage.nextPageUrl)
          setSearchNextPage({
            remainingCards: nextRes.data.data || [],
            nextPageUrl: nextRes.data.next_page || null,
          })
        } else {
          setSearchNextPage(null)
          setSearchHasMore(false)
        }
      } else if (searchNextPage.nextPageUrl) {
        // Fetch next page from Scryfall
        const nextRes = await Axios.get(searchNextPage.nextPageUrl)
        cardsToProcess = (nextRes.data.data || []).slice(0, 10)
        const remaining = (nextRes.data.data || []).slice(10)

        if (remaining.length > 0 || nextRes.data.has_more) {
          setSearchNextPage({
            remainingCards: remaining,
            nextPageUrl: nextRes.data.next_page || null,
          })
        } else {
          setSearchNextPage(null)
          setSearchHasMore(false)
        }
      }

      // Process and add cards
      const newResults = []
      for (const card of cardsToProcess) {
        const filename = `${card.name.replace(/[^a-zA-Z0-9]/g, '_')}_${card.set}`
        const imgUrl = card.image_uris?.normal || card.card_faces?.[0]?.image_uris?.normal

        if (imgUrl) {
          const localUrl = `${backendUrl}/card/${filename}`
          
          // Save card to backend (fire and forget)
          Axios.post(`${backendUrl}/cards/save`, {
            imgUrl,
            filename,
            setName: card.set_name,
            setCode: card.set,
          })
            .catch(() => {}) // Silent fail for caching

          newResults.push({
            name: card.name,
            url: localUrl,
            scryfallUrl: imgUrl,
            setCode: card.set,
            setName: card.set_name,
            collector_number: card.collector_number,
            colors: card.colors,
            cmc: card.cmc,
            type_line: card.type_line,
            mana_cost: card.mana_cost,
            rarity: card.rarity,
            artist: card.artist,
            source: 'scryfall',
          })
        }
      }

      setSearchResults(prev => [...prev, ...newResults])

      // Check if we've exhausted all results
      if (!searchNextPage?.remainingCards?.length && !searchNextPage?.nextPageUrl) {
        setSearchHasMore(false)
      }
    } catch (err) {
      console.error(`[Search] Error loading more:`, err)
    } finally {
      setLoadingMore(false)
    }
  }

  // Clear search and restore previous view
  const clearSearch = () => {
    setSearchQuery('')
    setSearchResults(null)
    setSearchOpen(false)
    if (previousSet) {
      setCurrentSet(previousSet)
      setPreviousSet(null)
    }
  }

  // ===== DECK BUILDER FUNCTIONS =====

  // Create a new deck
  const createNewDeck = async () => {
    const newDeck = {
      id: Date.now().toString(),
      name: `New Deck ${decks.length + 1}`,
      createdAt: new Date().toISOString(),
      commander: null, // Card object or null
      stacks: [
        {id: 'white', name: 'White', cards: []},
        {id: 'blue', name: 'Blue', cards: []},
        {id: 'black', name: 'Black', cards: []},
        {id: 'red', name: 'Red', cards: []},
        {id: 'green', name: 'Green', cards: []},
        {id: 'colorless', name: 'Colorless', cards: []},
        {id: 'lands', name: 'Lands', cards: []},
      ],
    }
    const updated = [...decks, newDeck]
    setDecks(updated)
    // Phase-out: persist synchronously (before the reload) and open the new deck in the tier-list editor.
    try {
      localStorage.setItem('mtgDecks', JSON.stringify(updated))
      localStorage.setItem('blogActiveDeckId', newDeck.id)
      localStorage.setItem('blogDeckFocus', '1')
    } catch (e) {}
    // SPA nav — but if logged in, make sure the new deck is on the server before the deck editor fetches it.
    if (currentUser) { try { await Axios.post('/api/decks', {decks: updated}) } catch (e) {} }
    history.push('/tierlist')
  }

  // Open an existing deck. Phase-out: open it in the tier-list editor (shared `mtgDecks` storage),
  // signalling the tier-list page to collapse its left column so the deck editor is the focus.
  const openDeck = async deck => {
    try {
      localStorage.setItem('blogActiveDeckId', deck.id)
      localStorage.setItem('blogDeckFocus', '1')
    } catch (e) {}
    history.push('/tierlist')
    return
    // eslint-disable-next-line no-unreachable
    setActiveDeck(deck)
    setUserMenuOpen(false)
    // Load saved sort order for this deck
    setStackSortOrder(deck.sortOrder || 'custom')
    // Save as last viewed deck and mark last view as deck
    localStorage.setItem('lastViewedDeck', deck.id)
    localStorage.setItem('lastViewType', 'deck')

    // Collect all cards that need price refresh
    const cardsToRefresh = []

    // Add cards from all stacks
    deck.stacks.forEach(stack => {
      stack.cards.forEach(card => {
        if (card.setCode && card.collector_number) {
          cardsToRefresh.push({
            setCode: card.setCode,
            collector_number: card.collector_number,
            deckCardId: card.deckCardId,
          })
        }
      })
    })

    // Add commander if exists
    if (deck.commander?.setCode && deck.commander?.collector_number) {
      cardsToRefresh.push({
        setCode: deck.commander.setCode,
        collector_number: deck.commander.collector_number,
        deckCardId: deck.commander.deckCardId,
      })
    }

    if (cardsToRefresh.length === 0) return

    console.log(`[Deck] Refreshing prices for ${cardsToRefresh.length} cards...`)

    try {
      const backendUrl = getBackendUrl()
      const res = await Axios.post(`${backendUrl}/previews/refresh-prices`, {
        cards: cardsToRefresh,
      })

      if (res.data.results) {
        // Build a map of deckCardId -> new prices
        const priceMap = {}
        res.data.results.forEach(r => {
          if (r.prices && r.deckCardId) {
            priceMap[r.deckCardId] = r.prices
          }
        })

        // Update deck with fresh prices
        const updatedDeck = {
          ...deck,
          stacks: deck.stacks.map(stack => ({
            ...stack,
            cards: stack.cards.map(card =>
              priceMap[card.deckCardId] ? {...card, prices: priceMap[card.deckCardId]} : card,
            ),
          })),
          commander:
            deck.commander && priceMap[deck.commander.deckCardId]
              ? {...deck.commander, prices: priceMap[deck.commander.deckCardId]}
              : deck.commander,
        }

        setActiveDeck(updatedDeck)
        setDecks(prevDecks => prevDecks.map(d => (d.id === deck.id ? updatedDeck : d)))

        const refreshedCount = Object.keys(priceMap).length
        console.log(`[Deck] Prices refreshed for ${refreshedCount}/${cardsToRefresh.length} cards`)
      }
    } catch (err) {
      console.log(`[Deck] Failed to refresh prices:`, err.message)
      // Continue with existing prices
    }
  }

  // Close deck builder
  const closeDeckBuilder = () => {
    setActiveDeck(null)
    setDeckSearchQuery('')
    setDeckSearchResults([])
    // Mark last view as set (going back to sets view)
    localStorage.setItem('lastViewType', 'set')
  }

  // Rename deck
  const renameDeck = (deckId, newName) => {
    setDecks(decks.map(d => (d.id === deckId ? {...d, name: newName} : d)))
    if (activeDeck?.id === deckId) {
      setActiveDeck({...activeDeck, name: newName})
    }
  }

  // Delete deck
  const deleteDeck = deckId => {
    if (window.confirm('Delete this deck?')) {
      setDecks(decks.filter(d => d.id !== deckId))
      if (activeDeck?.id === deckId) {
        setActiveDeck(null)
      }
    }
  }

  // Add a new custom stack to the active deck
  const addStack = () => {
    if (!activeDeck) return
    const newStackId = Date.now().toString()
    const newStack = {
      id: newStackId,
      name: 'New Stack',
      cards: [],
    }
    const updatedDeck = {
      ...activeDeck,
      stacks: [newStack, ...activeDeck.stacks],
    }
    setActiveDeck(updatedDeck)
    setDecks(decks.map(d => (d.id === activeDeck.id ? updatedDeck : d)))

    // Auto-scroll to the new stack and enter edit mode
    setTimeout(() => {
      if (stacksContainerRef.current) {
        stacksContainerRef.current.scrollTo({
          top: stacksContainerRef.current.scrollHeight,
          behavior: 'smooth',
        })
      }
      setEditingStackId(newStackId)
    }, 50)
  }

  // Rename a stack
  const renameStack = (stackId, newName) => {
    if (!activeDeck) return
    const updatedDeck = {
      ...activeDeck,
      stacks: activeDeck.stacks.map(s => (s.id === stackId ? {...s, name: newName} : s)),
    }
    setActiveDeck(updatedDeck)
    setDecks(decks.map(d => (d.id === activeDeck.id ? updatedDeck : d)))
  }

  // Delete a stack (move cards to first remaining stack, or create a new one)
  const deleteStack = stackId => {
    if (!activeDeck) return
    const stackToDelete = activeDeck.stacks.find(s => s.id === stackId)
    if (!stackToDelete) return

    // Find another stack to move cards to (first one that isn't being deleted)
    const remainingStacks = activeDeck.stacks.filter(s => s.id !== stackId)
    const targetStack = remainingStacks[0]

    const updatedDeck = {
      ...activeDeck,
      stacks: targetStack
        ? activeDeck.stacks
            .map(s => {
              if (s.id === targetStack.id) {
                return {...s, cards: [...s.cards, ...stackToDelete.cards]}
              }
              return s
            })
            .filter(s => s.id !== stackId)
        : [{id: 'main', name: 'Main', cards: stackToDelete.cards}], // If deleting last stack, create new one
    }
    setActiveDeck(updatedDeck)
    setDecks(decks.map(d => (d.id === activeDeck.id ? updatedDeck : d)))
  }

  // Optimize deck prices - find cheapest variants for all cards
  const optimizeDeckPrices = async () => {
    if (!activeDeck || optimizing) return

    // Collect all unique card names
    const cardsByName = {}
    activeDeck.stacks.forEach(stack => {
      stack.cards.forEach(card => {
        if (!cardsByName[card.name]) {
          cardsByName[card.name] = []
        }
        cardsByName[card.name].push({card, stackId: stack.id})
      })
    })
    if (activeDeck.commander) {
      if (!cardsByName[activeDeck.commander.name]) {
        cardsByName[activeDeck.commander.name] = []
      }
      cardsByName[activeDeck.commander.name].push({card: activeDeck.commander, isCommander: true})
    }

    const uniqueNames = Object.keys(cardsByName)
    if (uniqueNames.length === 0) return

    setOptimizing(true)
    setOptimizeProgress({current: 0, total: uniqueNames.length, saved: 0})

    let totalSaved = 0
    const replacements = {} // cardName -> cheapest card data

    console.log(`[Optimize] Starting optimization for ${uniqueNames.length} unique cards`)

    try {
      for (let i = 0; i < uniqueNames.length; i++) {
        const cardName = uniqueNames[i]
        setOptimizeProgress(prev => ({...prev, current: i + 1}))

        try {
          // Get the scryfallId from one of the cards with this name
          const sampleCard = cardsByName[cardName][0].card
          let cardData = null

          console.log(`[Optimize] ${i + 1}/${uniqueNames.length} Fetching prints for: "${cardName}"`)

          if (sampleCard.scryfallId) {
            // Use scryfallId if available
            const response = await fetch(`https://api.scryfall.com/cards/${sampleCard.scryfallId}`)
            if (response.ok) {
              cardData = await response.json()
            }
          }

          // Fallback: search by exact name if no scryfallId or fetch failed
          if (!cardData) {
            console.log(`[Optimize] Falling back to name search for "${cardName}"`)
            const nameResponse = await fetch(`https://api.scryfall.com/cards/named?exact=${encodeURIComponent(cardName)}`)
            if (nameResponse.ok) {
              cardData = await nameResponse.json()
            } else {
              console.log(`[Optimize] Failed to find card "${cardName}": ${nameResponse.status}`)
              continue
            }
          }

          // Get prints search URI
          if (!cardData?.prints_search_uri) {
            console.log(`[Optimize] No prints_search_uri for "${cardName}"`)
            continue
          }

          // Fetch all printings
          const printsResponse = await fetch(cardData.prints_search_uri)
          if (!printsResponse.ok) {
            console.log(`[Optimize] Failed to fetch prints for "${cardName}": ${printsResponse.status}`)
            continue
          }

          const printsData = await printsResponse.json()
          const variants = printsData.data || []
          console.log(`[Optimize] Found ${variants.length} variants for "${cardName}"`)

          if (!variants.length) continue

          // Find cheapest variant with a price
          let cheapest = null
          let cheapestPrice = Infinity
          variants.forEach(v => {
            const price = parseFloat(v.prices?.usd || '999999')
            if (price < cheapestPrice) {
              cheapestPrice = price
              cheapest = v
            }
          })

          if (cheapest && cheapestPrice < Infinity) {
            console.log(`[Optimize] Cheapest for "${cardName}": $${cheapestPrice} (${cheapest.set})`)
            // Calculate savings for all instances of this card
            // Also replace cards with no price if we found one with a price
            let shouldReplace = false
            cardsByName[cardName].forEach(({card}) => {
              const currentPrice = parseFloat(card.prices?.usd || '0')
              if (!card.prices?.usd) {
                // Card has no price, replace with one that does
                shouldReplace = true
              } else if (cheapestPrice < currentPrice) {
                // Found a cheaper variant
                totalSaved += (currentPrice - cheapestPrice)
                shouldReplace = true
              }
            })
            if (shouldReplace) {
              replacements[cardName] = cheapest
              console.log(`[Optimize] Will replace "${cardName}"`)
            }
          } else {
            console.log(`[Optimize] No priced variant found for "${cardName}"`)
          }
        } catch (cardErr) {
          console.error(`[Optimize] Error processing "${cardName}":`, cardErr)
        }

        // Delay to respect Scryfall rate limit (100ms between requests)
        await new Promise(r => setTimeout(r, 120))
      }

      // Apply all replacements (map Scryfall fields to our format)
      const updatedDeck = {
        ...activeDeck,
        stacks: activeDeck.stacks.map(stack => ({
          ...stack,
          cards: stack.cards.map(card => {
            if (replacements[card.name]) {
              const cheapest = replacements[card.name]
              const imageUrl = cheapest.image_uris?.normal || cheapest.image_uris?.small || card.url
              return {
                ...card,
                url: imageUrl,
                setCode: cheapest.set,
                setName: cheapest.set_name,
                prices: cheapest.prices,
                scryfallId: cheapest.id,
              }
            }
            return card
          }),
        })),
        commander: activeDeck.commander && replacements[activeDeck.commander.name]
          ? (() => {
              const cheapest = replacements[activeDeck.commander.name]
              const imageUrl = cheapest.image_uris?.normal || cheapest.image_uris?.small || activeDeck.commander.url
              return {
                ...activeDeck.commander,
                url: imageUrl,
                setCode: cheapest.set,
                setName: cheapest.set_name,
                prices: cheapest.prices,
                scryfallId: cheapest.id,
              }
            })()
          : activeDeck.commander,
      }

      setActiveDeck(updatedDeck)
      setDecks(decks.map(d => (d.id === activeDeck.id ? updatedDeck : d)))
      setOptimizeProgress(prev => ({...prev, saved: totalSaved}))

      console.log(`[Deck] Optimized prices, saved $${totalSaved.toFixed(2)}`)
    } catch (err) {
      console.error('[Deck] Optimize failed:', err)
    } finally {
      setTimeout(() => {
        setOptimizing(false)
        setOptimizeProgress({current: 0, total: 0, saved: 0})
      }, 2000) // Show final result for 2 seconds
    }
  }

  // Max cards per deck for performance
  const MAX_DECK_CARDS = 1000

  // Helper to count total cards in deck
  const getDeckCardCount = deck => {
    if (!deck) return 0
    const stackCards = (deck.stacks || []).reduce((sum, s) => sum + (s.cards?.length || 0), 0)
    return stackCards + (deck.commander ? 1 : 0)
  }

  // Last-known deck value: sums the stored card prices (persisted with the deck), so it reflects
  // whatever prices were saved without any fresh fetch. 0 when no card has a price.
  const getDeckPrice = deck => {
    if (!deck) return 0
    let total = 0
    ;(deck.stacks || []).forEach(s => (s.cards || []).forEach(c => { if (c.prices?.usd) total += parseFloat(c.prices.usd) || 0 }))
    if (deck.commander?.prices?.usd) total += parseFloat(deck.commander.prices.usd) || 0
    return total
  }

  const getDeckCoverUrl = deck => {
    const commander = deck?.commander
    return commander?.url || commander?.scryfallUrl || commander?.image_uris?.normal || commander?.image_uris?.large || null
  }

  const reorderDecks = (fromDeckId, toDeckId, position = 'before') => {
    if (!fromDeckId || !toDeckId || fromDeckId === toDeckId) return

    setDecks(prevDecks => {
      const fromIndex = prevDecks.findIndex(d => d.id === fromDeckId)
      const toIndex = prevDecks.findIndex(d => d.id === toDeckId)
      if (fromIndex === -1 || toIndex === -1) return prevDecks

      const nextDecks = [...prevDecks]
      const [movedDeck] = nextDecks.splice(fromIndex, 1)
      const insertIndex = nextDecks.findIndex(d => d.id === toDeckId)
      nextDecks.splice(position === 'after' ? insertIndex + 1 : insertIndex, 0, movedDeck)
      return nextDecks
    })
  }

  // Add card to deck (auto-assign to color stack)
  const addCardToDeck = async card => {
    if (!activeDeck) return

    // Check card limit
    if (getDeckCardCount(activeDeck) >= MAX_DECK_CARDS) {
      console.log(`[Deck] Cannot add card: deck has reached ${MAX_DECK_CARDS} card limit`)
      return
    }

    // Determine which stack based on card type/color
    let targetStackId = 'colorless'
    const colors = card.colors || []
    const typeLine = card.type_line?.toLowerCase() || ''

    // Lands go to Lands stack
    if (typeLine.includes('land')) {
      targetStackId = 'lands'
    }
    // Single color cards go to their color stack
    else if (colors.length === 1) {
      const colorMap = {W: 'white', U: 'blue', B: 'black', R: 'red', G: 'green'}
      targetStackId = colorMap[colors[0]] || 'colorless'
    }
    // Multicolor cards - put in first color's stack for now
    else if (colors.length > 1) {
      const colorMap = {W: 'white', U: 'blue', B: 'black', R: 'red', G: 'green'}
      targetStackId = colorMap[colors[0]] || 'colorless'
    }
    // Colorless (no colors, not a land) goes to Colorless
    // This is the default

    let cardToAdd = {...card, deckCardId: Date.now().toString()}

    // If card is from Scryfall, cache it locally in background
    if (card.source === 'scryfall') {
      try {
        const backendUrl = getBackendUrl()
        const cacheRes = await Axios.post(`${backendUrl}/previews/cache-card`, {
          name: card.name,
          setCode: card.setCode,
          collector_number: card.collector_number,
          scryfallUrl: card.url,
          colors: card.colors,
          cmc: card.cmc,
          type_line: card.type_line,
          mana_cost: card.mana_cost,
          rarity: card.rarity,
          prices: card.prices,
        })
        // Update URL to local cached version
        if (cacheRes.data.localUrl) {
          cardToAdd.url = cacheRes.data.localUrl
          cardToAdd.source = 'local'
          console.log(`[Deck] Cached card locally: ${card.name}`)
        }
      } catch (err) {
        console.log(`[Deck] Failed to cache card, using Scryfall URL:`, err.message)
        // Keep using Scryfall URL if caching fails
      }
    }

    const cardWithId = cardToAdd

    const updatedDeck = {
      ...activeDeck,
      stacks: activeDeck.stacks.map(s => (s.id === targetStackId ? {...s, cards: [...s.cards, cardWithId]} : s)),
    }
    setActiveDeck(updatedDeck)
    setDecks(decks.map(d => (d.id === activeDeck.id ? updatedDeck : d)))
  }

  // Add card to a specific stack (used by drag-drop from search results)
  const addCardToStack = async (card, stackId) => {
    if (!activeDeck) return

    // Check card limit
    if (getDeckCardCount(activeDeck) >= MAX_DECK_CARDS) {
      console.log(`[Deck] Cannot add card: deck has reached ${MAX_DECK_CARDS} card limit`)
      return
    }

    let cardToAdd = {...card, deckCardId: Date.now().toString()}

    // If card is from Scryfall, cache it locally
    if (card.source === 'scryfall') {
      try {
        const backendUrl = getBackendUrl()
        const cacheRes = await Axios.post(`${backendUrl}/previews/cache-card`, {
          name: card.name,
          setCode: card.setCode,
          collector_number: card.collector_number,
          scryfallUrl: card.url,
          colors: card.colors,
          cmc: card.cmc,
          type_line: card.type_line,
          mana_cost: card.mana_cost,
          rarity: card.rarity,
          prices: card.prices,
        })
        if (cacheRes.data.localUrl) {
          cardToAdd.url = cacheRes.data.localUrl
          cardToAdd.source = 'local'
          console.log(`[Deck] Cached card locally: ${card.name}`)
        }
      } catch (err) {
        console.log(`[Deck] Failed to cache card, using Scryfall URL:`, err.message)
      }
    }

    const updatedDeck = {
      ...activeDeck,
      stacks: activeDeck.stacks.map(s => (s.id === stackId ? {...s, cards: [...s.cards, cardToAdd]} : s)),
    }
    setActiveDeck(updatedDeck)
    setDecks(decks.map(d => (d.id === activeDeck.id ? updatedDeck : d)))
  }

  // Remove card from deck
  const removeCardFromDeck = (stackId, cardDeckId) => {
    if (!activeDeck) return
    const updatedDeck = {
      ...activeDeck,
      stacks: activeDeck.stacks.map(s =>
        s.id === stackId ? {...s, cards: s.cards.filter(c => c.deckCardId !== cardDeckId)} : s,
      ),
    }
    setActiveDeck(updatedDeck)
    setDecks(decks.map(d => (d.id === activeDeck.id ? updatedDeck : d)))
  }

  // Update a deck card's variant (art/printing)
  const updateDeckCardVariant = (stackId, deckCardId, variant) => {
    if (!activeDeck) return
    const updatedDeck = {
      ...activeDeck,
      stacks: activeDeck.stacks.map(s =>
        s.id === stackId
          ? {
              ...s,
              cards: s.cards.map(c =>
                c.deckCardId === deckCardId
                  ? {
                      ...c,
                      url: variant.image_uri,
                      setCode: variant.set,
                      setName: variant.set_name,
                      collector_number: variant.collector_number,
                      prices: variant.usd ? {usd: variant.usd, usd_foil: variant.usd_foil} : c.prices,
                    }
                  : c,
              ),
            }
          : s,
      ),
    }
    setActiveDeck(updatedDeck)
    setDecks(decks.map(d => (d.id === activeDeck.id ? updatedDeck : d)))
  }

  // Import deck from text list
  const importDeckList = async () => {
    if (!activeDeck || !importText.trim()) return

    setImporting(true)
    setImportProgress({current: 0, total: 0, status: 'Parsing card list...'})

    try {
      // Parse the card list - format: "quantity cardname" or just "cardname"
      const lines = importText
        .trim()
        .split('\n')
        .filter(line => line.trim())
      const cardRequests = []

      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue

        // Match "number cardname" or just "cardname"
        const match = trimmed.match(/^(\d+)\s+(.+)$/)
        let quantity = 1
        let cardName = trimmed

        if (match) {
          quantity = parseInt(match[1], 10)
          cardName = match[2].trim()
        }

        // Limit quantity to reasonable amount
        quantity = Math.min(quantity, 99)

        for (let i = 0; i < quantity; i++) {
          cardRequests.push(cardName)
        }
      }

      // Limit total cards
      const maxImport = Math.min(cardRequests.length, 1000)
      const toImport = cardRequests.slice(0, maxImport)

      setImportProgress({current: 0, total: toImport.length, status: 'Searching for cards...'})

      const backendUrl = getBackendUrl()
      const foundCards = []
      const notFound = []

      // Process in batches to avoid overwhelming the server
      const batchSize = 10
      for (let i = 0; i < toImport.length; i += batchSize) {
        const batch = toImport.slice(i, i + batchSize)

        // Search for each card in the batch
        const batchPromises = batch.map(async cardName => {
          try {
            // Search for the card
            const res = await Axios.get(`${backendUrl}/previews/search?q=${encodeURIComponent(cardName)}`)
            const results = res.data.results || []

            // Find exact match (case insensitive)
            const exactMatch = results.find(r => r.name.toLowerCase() === cardName.toLowerCase())

            if (exactMatch) {
              return {found: true, card: exactMatch}
            } else if (results.length > 0) {
              // Use first result if no exact match
              return {found: true, card: results[0]}
            }
            return {found: false, cardName}
          } catch (err) {
            return {found: false, cardName}
          }
        })

        const batchResults = await Promise.all(batchPromises)

        for (const result of batchResults) {
          if (result.found) {
            foundCards.push(result.card)
          } else {
            notFound.push(result.cardName)
          }
        }

        setImportProgress({
          current: Math.min(i + batchSize, toImport.length),
          total: toImport.length,
          status: `Found ${foundCards.length} cards...`,
        })

        // Small delay between batches to be nice to the server
        if (i + batchSize < toImport.length) {
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      }

      setImportProgress({current: toImport.length, total: toImport.length, status: 'Adding cards to deck...'})

      // Find or create an "Imported" stack
      let importStackId = 'imported'
      let updatedDeck = {...activeDeck}

      // Check if imported stack exists
      const existingImportStack = updatedDeck.stacks.find(s => s.id === 'imported')
      if (!existingImportStack) {
        updatedDeck = {
          ...updatedDeck,
          stacks: [...updatedDeck.stacks, {id: 'imported', name: 'Imported', cards: []}],
        }
      }

      // Add all found cards to the imported stack
      const cardsToAdd = foundCards.map(card => ({
        ...card,
        deckCardId: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      }))

      updatedDeck = {
        ...updatedDeck,
        stacks: updatedDeck.stacks.map(s => (s.id === importStackId ? {...s, cards: [...s.cards, ...cardsToAdd]} : s)),
      }

      setActiveDeck(updatedDeck)
      setDecks(decks.map(d => (d.id === activeDeck.id ? updatedDeck : d)))

      const successMsg =
        `Imported ${foundCards.length} cards` + (notFound.length > 0 ? ` (${notFound.length} not found)` : '')
      setImportProgress({current: toImport.length, total: toImport.length, status: successMsg})

      // Log not found cards
      if (notFound.length > 0) {
        console.log('[Import] Cards not found:', notFound)
      }

      // Auto-close after success
      setTimeout(() => {
        setShowImportPanel(false)
        setImportText('')
        setImporting(false)
        setImportProgress({current: 0, total: 0, status: ''})
      }, 2000)
    } catch (err) {
      console.error('[Import] Error:', err)
      setImportProgress({current: 0, total: 0, status: `Error: ${err.message}`})
      setImporting(false)
    }
  }

  // Export deck to clipboard
  const exportDeckToClipboard = () => {
    if (!activeDeck) return

    // Build deck list text - count duplicates by name
    const cardCounts = {}
    activeDeck.stacks.forEach(stack => {
      stack.cards.forEach(card => {
        const name = card.name
        cardCounts[name] = (cardCounts[name] || 0) + 1
      })
    })

    // Add commander if present
    if (activeDeck.commander) {
      const name = activeDeck.commander.name
      cardCounts[name] = (cardCounts[name] || 0) + 1
    }

    // Format as "quantity cardname" lines
    const lines = Object.entries(cardCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, count]) => `${count} ${name}`)

    const deckText = lines.join('\n')

    navigator.clipboard.writeText(deckText).then(() => {
      setShowExportToast(true)
      setTimeout(() => setShowExportToast(false), 2000)
    })
  }

  // Submit AI question
  const submitAiQuestion = async () => {
    if (!aiQuestion.trim() || aiAsking) return

    console.log('[AI Question] Submitting:', aiQuestion)
    setAiAsking(true)
    setAiCards([])
    setHoveredAiCard(null)

    try {
      // Build deck list from active deck
      let deckList = ''
      let commander = ''
      let colorIdentity = ''

      if (activeDeck) {
        // Get all card names
        const cardNames = []
        activeDeck.stacks.forEach(stack => {
          stack.cards.forEach(card => {
            cardNames.push(card.name)
          })
        })
        deckList = cardNames.join('\n')

        // Get commander info
        if (activeDeck.commander) {
          commander = activeDeck.commander.name
          colorIdentity = activeDeck.commander.colorIdentity?.join('') || ''
        }
      }

      console.log('[AI Question] Deck list:', deckList.split('\n').filter(Boolean).length, 'cards')
      console.log('[AI Question] Commander:', commander)
      console.log('[AI Question] Color identity:', colorIdentity)

      const res = await Axios.post('/api/ai-question', {
        question: aiQuestion,
        deckList,
        commander,
        colorIdentity,
        reasoningEffort: aiReasoningEffort
      })
      console.log('[AI Question] Full Response:', res.data)
      setAiResponse(res.data)
      setAiQuestion('')

      // Resolve card images if we got cards
      if (res.data.cards && res.data.cards.length > 0) {
        resolveAiCards(res.data.cards)
      }
    } catch (err) {
      console.error('[AI Question] Error:', err)
    } finally {
      setAiAsking(false)
    }
  }

  // Move card between stacks (drag & drop)
  const moveCard = (cardDeckId, fromStackId, toStackId) => {
    if (!activeDeck || fromStackId === toStackId) return

    const fromStack = activeDeck.stacks.find(s => s.id === fromStackId)
    const cardToMove = fromStack?.cards.find(c => c.deckCardId === cardDeckId)
    if (!cardToMove) return

    const updatedDeck = {
      ...activeDeck,
      stacks: activeDeck.stacks.map(s => {
        if (s.id === fromStackId) {
          return {...s, cards: s.cards.filter(c => c.deckCardId !== cardDeckId)}
        }
        if (s.id === toStackId) {
          return {...s, cards: [...s.cards, cardToMove]}
        }
        return s
      }),
    }
    setActiveDeck(updatedDeck)
    setDecks(decks.map(d => (d.id === activeDeck.id ? updatedDeck : d)))
  }

  // Set commander (removes card from its stack)
  const setCommander = (card, fromStackId) => {
    if (!activeDeck) return

    // Remove card from its stack and set as commander
    const updatedDeck = {
      ...activeDeck,
      commander: card,
      stacks: activeDeck.stacks.map(s =>
        s.id === fromStackId ? {...s, cards: s.cards.filter(c => c.deckCardId !== card.deckCardId)} : s,
      ),
    }
    setActiveDeck(updatedDeck)
    setDecks(decks.map(d => (d.id === activeDeck.id ? updatedDeck : d)))
  }

  // Clear commander (optionally move back to a stack)
  const clearCommander = () => {
    if (!activeDeck || !activeDeck.commander) return

    // Move commander back to colorless stack
    const commander = activeDeck.commander
    const updatedDeck = {
      ...activeDeck,
      commander: null,
      stacks: activeDeck.stacks.map(s => (s.id === 'colorless' ? {...s, cards: [...s.cards, commander]} : s)),
    }
    setActiveDeck(updatedDeck)
    setDecks(decks.map(d => (d.id === activeDeck.id ? updatedDeck : d)))
  }

  // Reorder stacks (drag entire stack to new position)
  const reorderStack = (fromIndex, toIndex) => {
    if (!activeDeck || fromIndex === toIndex) return
    const newStacks = [...activeDeck.stacks]
    const [movedStack] = newStacks.splice(fromIndex, 1)
    // When moving left-to-right, adjust toIndex since removal shifts indices
    const adjustedIndex = fromIndex < toIndex ? toIndex - 1 : toIndex
    newStacks.splice(adjustedIndex, 0, movedStack)
    const updatedDeck = {...activeDeck, stacks: newStacks}
    setActiveDeck(updatedDeck)
    setDecks(decks.map(d => (d.id === activeDeck.id ? updatedDeck : d)))
  }

  // Search cards for deck builder
  const searchForDeck = async () => {
    if (!deckSearchQuery.trim()) return
    setDeckSearching(true)

    try {
      const backendUrl = getBackendUrl()
      const res = await Axios.get(`${backendUrl}/previews/search?q=${encodeURIComponent(deckSearchQuery.trim())}`)
      // Search endpoint returns 'results' not 'cards'
      setDeckSearchResults(res.data.results || [])
    } catch (err) {
      console.error('[Deck Search] Error:', err)
      setDeckSearchResults([])
    } finally {
      setDeckSearching(false)
    }
  }

  // ===== END DECK BUILDER FUNCTIONS =====

  const cardWidth = isMobile ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP
  const cardHeight = isMobile ? CARD_HEIGHT_MOBILE : CARD_HEIGHT_DESKTOP

  // Color order for sorting (WUBRG + multicolor, colorless, land)
  const COLOR_ORDER = {W: 0, U: 1, B: 2, R: 3, G: 4}

  const getColorSortValue = card => {
    // Lands go last
    if (card.type_line?.toLowerCase().includes('land')) return 100

    const colors = card.colors || []

    // Colorless
    if (colors.length === 0) return 90

    // Multicolor
    if (colors.length > 1) return 80

    // Single color - use WUBRG order
    return COLOR_ORDER[colors[0]] ?? 50
  }

  // Sort cards based on selected sort option
  const sortCards = cards => {
    if (!cards || cards.length === 0) return cards

    const sorted = [...cards]

    switch (sortBy) {
      case 'color-cmc':
        // Sort by color first, then by CMC within each color
        sorted.sort((a, b) => {
          const colorA = getColorSortValue(a)
          const colorB = getColorSortValue(b)
          if (colorA !== colorB) return colorA - colorB
          // Same color group - sort by CMC
          const cmcA = parseFloat(a.cmc) || 0
          const cmcB = parseFloat(b.cmc) || 0
          return cmcA - cmcB
        })
        break

      case 'cmc':
        sorted.sort((a, b) => {
          const cmcA = parseFloat(a.cmc) || 0
          const cmcB = parseFloat(b.cmc) || 0
          return cmcA - cmcB
        })
        break

      case 'name':
        sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        break

      case 'collector':
        sorted.sort((a, b) => {
          const numA = parseInt(a.collector_number) || 0
          const numB = parseInt(b.collector_number) || 0
          return numA - numB
        })
        break

      case 'rarity':
        const rarityOrder = {mythic: 0, rare: 1, uncommon: 2, common: 3}
        sorted.sort((a, b) => {
          const rarA = rarityOrder[a.rarity] ?? 4
          const rarB = rarityOrder[b.rarity] ?? 4
          if (rarA !== rarB) return rarA - rarB
          // Same rarity - sort by CMC
          return (parseFloat(a.cmc) || 0) - (parseFloat(b.cmc) || 0)
        })
        break

      default:
        break
    }

    return sorted
  }

  // Filter cards by type
  const filterByType = cards => {
    if (typeFilter === 'all') return cards
    return cards.filter(card => {
      const typeLine = (card.type_line || '').toLowerCase()
      switch (typeFilter) {
        case 'creature':
          return typeLine.includes('creature')
        case 'instant':
          return typeLine.includes('instant')
        case 'sorcery':
          return typeLine.includes('sorcery')
        case 'artifact':
          return typeLine.includes('artifact') && !typeLine.includes('creature')
        case 'enchantment':
          return typeLine.includes('enchantment') && !typeLine.includes('creature')
        case 'planeswalker':
          return typeLine.includes('planeswalker')
        case 'land':
          return typeLine.includes('land')
        default:
          return true
      }
    })
  }

  // What cards to display (filtered then sorted)
  const rawCards = searchResults || currentSet?.cards || []
  const filteredCards = filterByType(rawCards)
  const displayCards = sortCards(filteredCards)
  const displayTitle = searchResults
    ? `Search: "${searchQuery}" (${searchResults.length} results)`
    : currentSet?.setName || 'Card Previews'

  // Theme colors
  const theme = {
    bg: darkMode ? '#1a1a1a' : '#f5f5f5',
    bgSecondary: darkMode ? '#333' : '#ddd',
    bgTertiary: darkMode ? '#444' : '#ccc',
    text: darkMode ? '#fff' : '#222',
    textSecondary: darkMode ? '#888' : '#666',
    border: darkMode ? '#333' : '#ccc',
  }

  // Small bordered-gray icon button, matching the tier-list deck-panel settings/user buttons.
  const iconBtn = {
    background: 'transparent',
    border: `1px solid ${theme.bgTertiary}`,
    borderRadius: 3,
    width: 34,
    height: 32,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
    padding: 0,
  }

  return (
    <div
      style={{
        background: theme.bg,
        minHeight: '100vh',
        padding: isMobile ? '16px 8px' : '44px 32px 32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
      }}>
      {/* Global styles */}
      <style>{globalStyles}</style>

      {/* Board overlay */}
      {boardOpen && <Board onClose={() => setBoardOpen(false)} />}

      {/* Hamburger button */}
      {/* Row 1, left: Home (active — you're on the set viewer) */}
      <span
        style={{...iconBtn, position: 'fixed', top: 4, left: 4, zIndex: 100, borderColor: '#4da3ff', color: '#4da3ff', cursor: 'default'}}
        title="Home (current page)">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5 9.5V21h14V9.5" />
        </svg>
      </span>
      {/* Row 2, left: Sets menu (the hamburger, moved down one slot) */}
      <button
        onClick={() => setMenuOpen(true)}
        className="blueHover"
        title="Sets"
        style={{...iconBtn, position: 'fixed', top: 40, left: 4, zIndex: 100, flexDirection: 'column', gap: 4}}>
        <div style={{width: 16, height: 2, background: 'currentColor'}} />
        <div style={{width: 16, height: 2, background: 'currentColor'}} />
        <div style={{width: 16, height: 2, background: 'currentColor'}} />
      </button>

      {/* Tier List button → /tierlist (SPA push; href kept for middle-click / open-in-new-tab) */}
      <a
        href="/tierlist"
        onClick={e => { try { localStorage.setItem('blogDeckFocus', '0') } catch (err) {} /* this button always opens the tier list itself, never the collapsed deck-focus view (that's for opening a deck from the profile) */ if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; e.preventDefault(); history.push('/tierlist') }}
        title="Tier List"
        className="blueHover"
        style={{...iconBtn, position: 'fixed', top: 4, left: 42, zIndex: 100, textDecoration: 'none'}}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
          <rect x="2" y="2" width="6" height="6" rx="1" />
          <rect x="2" y="9" width="6" height="6" rx="1" />
          <rect x="9" y="9" width="6" height="6" rx="1" />
          <rect x="2" y="16" width="6" height="6" rx="1" />
          <rect x="9" y="16" width="6" height="6" rx="1" />
          <rect x="16" y="16" width="6" height="6" rx="1" />
        </svg>
      </a>

      {/* Search button / input */}
      <div
        style={{
          position: 'fixed',
          top: 4,
          right: 4,
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}>
        {/* Search area — LEFTMOST; the input is always revealed (no open/close, no × needed).
            Emptying the box restores the set you were viewing. */}
        <input
          type="text"
          value={searchQuery}
          onChange={e => {
            const v = e.target.value
            setSearchQuery(v)
            if (!v.trim() && searchResults) clearSearch()
          }}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          onFocus={e => e.target.select()}
          placeholder="Card name..."
          autoFocus={!isMobile}
          spellCheck={false}
          style={{
            width: isMobile ? 130 : 170,
            height: 32,
            padding: '0 10px',
            fontSize: 13,
            border: `1px solid ${theme.bgTertiary}`,
            borderRadius: 3,
            background: theme.bgSecondary,
            color: theme.text,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        {searching ? (
          <div style={{...iconBtn, cursor: 'default'}}>
            <div
              style={{
                width: 15,
                height: 15,
                border: '2px solid #444',
                borderTopColor: '#fff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
          </div>
        ) : (
          <button
            onClick={handleSearch}
            className="blueHover"
            style={iconBtn}
            title="Search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </button>
        )}
        {/* Board button (＋) — MIDDLE, always visible */}
        <button
          onClick={() => setBoardOpen(true)}
          className="blueHover"
          style={iconBtn}
          title="Open board">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
        </button>
        {/* User profile button — RIGHT, always visible */}
        <button
          onClick={() => setUserMenuOpen(true)}
          className="blueHover"
          style={iconBtn}
          title="My Decks">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" />
          </svg>
        </button>
        {/* Login button — only when logged out; logged-in uses the profile icon + panel logout */}
        {!currentUser && (
          <button
            onClick={() => setShowLoginModal(true)}
            className="blueHover"
            style={{...iconBtn, width: 'auto', padding: '0 12px', fontSize: 13, fontWeight: 500}}
            title="Login">
            Login
          </button>
        )}
      </div>

      {/* Overlay */}
      {menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 200,
          }}
        />
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <>
          <div
            onClick={() => setShowLoginModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              zIndex: 500,
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: theme.bg,
              borderRadius: 8,
              padding: 24,
              zIndex: 501,
              width: 300,
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            }}>
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <span
                onClick={() => setLoginMode('login')}
                style={{
                  padding: '8px 16px',
                  cursor: 'pointer',
                  borderBottom: loginMode === 'login' ? `2px solid ${theme.accent}` : '2px solid transparent',
                  color: loginMode === 'login' ? theme.text : theme.textSecondary,
                }}>
                Login
              </span>
              <span
                onClick={() => setLoginMode('register')}
                style={{
                  padding: '8px 16px',
                  cursor: 'pointer',
                  borderBottom: loginMode === 'register' ? `2px solid ${theme.accent}` : '2px solid transparent',
                  color: loginMode === 'register' ? theme.text : theme.textSecondary,
                }}>
                Register
              </span>
            </div>
            <input
              type="text"
              value={loginUsername}
              onChange={e => setLoginUsername(e.target.value)}
              placeholder="Username"
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                marginBottom: 12,
                background: theme.bgSecondary,
                border: 'none',
                borderRadius: 4,
                color: theme.text,
                fontSize: 14,
                boxSizing: 'border-box',
              }}
            />
            <input
              type="password"
              value={loginPassword}
              onChange={e => setLoginPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAuth()}
              placeholder="Password"
              style={{
                width: '100%',
                padding: '10px 12px',
                marginBottom: 16,
                background: theme.bgSecondary,
                border: 'none',
                borderRadius: 4,
                color: theme.text,
                fontSize: 14,
                boxSizing: 'border-box',
              }}
            />
            {loginError && (
              <div style={{ color: '#f44', marginBottom: 12, fontSize: 13, textAlign: 'center' }}>
                {loginError}
              </div>
            )}
            <button
              onClick={handleAuth}
              disabled={loginLoading}
              style={{
                width: '100%',
                padding: '10px 16px',
                background: theme.accent,
                border: 'none',
                borderRadius: 4,
                color: '#fff',
                fontSize: 14,
                fontWeight: 500,
                cursor: loginLoading ? 'wait' : 'pointer',
                opacity: loginLoading ? 0.7 : 1,
              }}>
              {loginLoading ? 'Loading...' : (loginMode === 'login' ? 'Login' : 'Register')}
            </button>
            <button
              onClick={() => setShowLoginModal(false)}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: 'transparent',
                border: 'none',
                color: theme.textSecondary,
                fontSize: 20,
                cursor: 'pointer',
              }}>
              ×
            </button>
          </div>
        </>
      )}

      {/* Slide-out menu */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: menuOpen ? 0 : '-70vw',
          width: '70vw',
          maxWidth: 300,
          height: '100vh',
          background: theme.bg,
          zIndex: 300,
          transition: 'left 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: menuOpen ? '4px 0 20px rgba(0,0,0,0.5)' : 'none',
        }}>
        {/* Menu header with search */}
        <div
          style={{
            padding: '7px 10px',
            borderBottom: `1px solid ${theme.border}`,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}>
          <input
            type="text"
            value={menuFilter}
            onChange={e => setMenuFilter(e.target.value)}
            placeholder="Arabian Nights"
            spellCheck={false}
            style={{
              flex: 1,
              padding: '6px 10px',
              fontSize: 14,
              border: 'none',
              borderRadius: 3,
              background: theme.bgSecondary,
              color: theme.text,
              outline: 'none',
            }}
          />
          <button
            onClick={() => {
              setMenuOpen(false)
              setMenuFilter('')
            }}
            style={{
              background: 'none',
              border: 'none',
              color: theme.textSecondary,
              fontSize: 20,
              cursor: 'pointer',
              padding: '0 6px',
              flexShrink: 0,
            }}>
            ×
          </button>
        </div>

        {/* Set list */}
        <div
          className="set-menu-scroll"
          ref={setsScrollRef}
          onScroll={e => { try { localStorage.setItem('setsMenuScroll', String(e.currentTarget.scrollTop)) } catch (err) {} }}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '4px 0',
          }}>
          {(() => {
            const filteredSets = allSetsList
              .map(set => {
                if (!menuFilter.trim()) return {...set, matchScore: 0}
                const query = menuFilter.trim()
                const nameScore = fuzzyMatch(query, set.setName)
                const codeScore = fuzzyMatch(query, set.setCode)
                const bestScore =
                  nameScore === -1 ? codeScore : codeScore === -1 ? nameScore : Math.min(nameScore, codeScore)
                return {...set, matchScore: bestScore}
              })
              .filter(set => !menuFilter.trim() || set.matchScore !== -1)
              .sort((a, b) => {
                if (menuFilter.trim()) return a.matchScore - b.matchScore
                return 0
              })

            // Track last year to insert dividers (only when not filtering)
            let lastYear = null
            const elements = []
            const currentYear = new Date().getFullYear()
            // Check if there are any future year sets
            const hasFutureYear = filteredSets.some(s => s.releasedAt && new Date(s.releasedAt).getFullYear() > currentYear)

            for (const set of filteredSets) {
              const setYear = set.releasedAt ? new Date(set.releasedAt).getFullYear() : null

              // Insert year divider if year changed (only when not filtering)
              // Skip current year divider unless there's a future year set
              const skipCurrentYear = setYear === currentYear && !hasFutureYear
              if (!menuFilter.trim() && setYear && setYear !== lastYear && !skipCurrentYear) {
                elements.push(
                  <div
                    key={`year-${setYear}`}
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      padding: '3px 10px',
                      textAlign: 'right',
                      color: theme.textSecondary,
                      fontSize: 11,
                      fontWeight: 500,
                      borderBottom: `1px solid ${theme.border}`,
                      background: theme.bgSecondary,
                    }}>
                    {setYear}
                  </div>,
                )
                lastYear = setYear
              } else if (setYear !== lastYear) {
                lastYear = setYear // Still track year change even if we didn't show divider
              }

              elements.push(
                <button
                  key={set.setCode}
                  onClick={() => loadSet(set.setCode, set.setName)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    padding: '5px 10px',
                    background: currentSet?.setCode === set.setCode ? theme.bgSecondary : 'transparent',
                    border: 'none',
                    borderBottom: `1px solid ${theme.border}`,
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = theme.bgSecondary)}
                  onMouseLeave={e =>
                    (e.currentTarget.style.background =
                      currentSet?.setCode === set.setCode ? theme.bgSecondary : 'transparent')
                  }>
                  <img
                    src={`${getBackendUrl()}/set-icon/${set.setCode}`}
                    alt=""
                    style={{
                      width: 20,
                      height: 20,
                      filter: darkMode ? 'invert(1)' : 'none',
                      opacity: 0.7,
                      flexShrink: 0,
                    }}
                    onError={e => {
                      e.target.style.display = 'none'
                    }}
                  />
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={{color: theme.text, fontSize: 13, marginBottom: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{set.setName}</div>
                    <div style={{color: theme.textSecondary, fontSize: 10.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                      {set.setCode.toUpperCase()} • {set.cardCount} 🂠 • {new Date(set.releasedAt).toLocaleDateString()}
                      {set.boxEV && ` • ~$${set.boxEV}`}
                    </div>
                  </div>
                </button>,
              )
            }

            return elements
          })()}
        </div>
      </div>

      {/* Header — desktop: a fixed top bar (top edge, between the nav clusters); mobile: in-flow */}
      <div style={isMobile
        ? {display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', width: '100%', maxWidth: 1200, marginBottom: 16, paddingTop: 36}
        : {position: 'fixed', top: 4, left: 86, zIndex: 90, display: 'flex', alignItems: 'center', gap: 14, maxWidth: 'calc(100vw - 250px)'}}>
        <div style={{display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', minWidth: 0}}>
          <h1 style={{color: theme.text, fontSize: 18, fontWeight: 500, margin: 0, whiteSpace: 'nowrap'}}>
            {displayTitle}
          </h1>
        {!searchResults && currentSet && (
          <div style={{color: theme.textSecondary, fontSize: isMobile ? 12 : 14}}>
            {currentSet.setCode.toUpperCase()} • {currentSet.totalCards} cards
            {currentSet.releasedAt && ` • ${new Date(currentSet.releasedAt).toLocaleDateString()}`}
            {(() => {
              // Calculate Total Set Value and Box EV
              const cards = currentSet.cards || []
              if (cards.length === 0) return null

              // Total Set Value - sum of all card prices
              let totalValue = 0
              cards.forEach(card => {
                if (card.prices?.usd) {
                  totalValue += parseFloat(card.prices.usd)
                }
              })

              // Box EV (Dawnglare style) - based on pull rates
              // Get ALL cards by rarity, then calculate average including $0 for cards under $1
              const allMythics = cards.filter(c => c.rarity === 'mythic')
              const allRares = cards.filter(c => c.rarity === 'rare')

              // Sum values (treating cards under $1 as $0 per Dawnglare method)
              const mythicSum = allMythics.reduce((sum, c) => {
                const price = parseFloat(c.prices?.usd || 0)
                return sum + (price >= 1 ? price : 0)
              }, 0)
              const rareSum = allRares.reduce((sum, c) => {
                const price = parseFloat(c.prices?.usd || 0)
                return sum + (price >= 1 ? price : 0)
              }, 0)

              // Average value per rarity slot (divide by TOTAL cards in rarity, not just expensive ones)
              const avgMythic = allMythics.length > 0 ? mythicSum / allMythics.length : 0
              const avgRare = allRares.length > 0 ? rareSum / allRares.length : 0

              // Pack EV: 1/8 mythic, 7/8 rare (ignoring uncommons - rarely worth $1+)
              const packEV = (avgMythic * 0.125) + (avgRare * 0.875)
              const boxEV = packEV * 36 // standard 36 packs per box

              if (totalValue === 0) return null

              return (
                <>
                  {' • '}
                  <span style={{color: '#4ade80'}}>${totalValue.toFixed(0)} total</span>
                  {boxEV > 0 && (
                    <>
                      {' • '}
                      <span style={{color: '#60a5fa'}}>~${boxEV.toFixed(0)} box EV</span>
                    </>
                  )}
                </>
              )
            })()}
          </div>
        )}
        {searchResults && (
          <div style={{color: theme.textSecondary, fontSize: isMobile ? 12 : 14, display: 'flex', alignItems: 'center', gap: 8}}>
            <span>
              {searchResults.length} art{searchResults.length !== 1 ? 's' : ''} from{' '}
              {[...new Set(searchResults.map(c => c.setName))].slice(0, 5).join(', ')}
              {[...new Set(searchResults.map(c => c.setName))].length > 5 && '...'}
              {searchHasMore && ' (more available)'}
            </span>
            <button
              onClick={clearSearch}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.textSecondary,
                cursor: 'pointer',
                padding: '2px 6px',
                fontSize: 16,
                lineHeight: 1,
                borderRadius: 3,
              }}
              title="Clear search"
            >
              ×
            </button>
          </div>
        )}

        </div>
        {/* Sort/type/layout — on the same row as the title */}
        {displayCards.length > 0 && (
          <div style={{display: 'flex', gap: 12, flexWrap: 'wrap', flexShrink: 0}}>
            {/* Sort dropdown */}
            {isMobile ? (
              <button
                onClick={() => setActiveDropdown('sort')}
                style={{
                  padding: '10px 16px',
                  fontSize: 14,
                  border: 'none',
                  borderRadius: 3,
                  background: theme.bgSecondary,
                  color: theme.text,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}>
                {sortBy === 'color-cmc'
                  ? 'Color → CMC'
                  : sortBy === 'cmc'
                  ? 'Mana Cost'
                  : sortBy === 'name'
                  ? 'Name'
                  : sortBy === 'collector'
                  ? 'Collector #'
                  : 'Rarity'}
                <span style={{fontSize: 10}}>▼</span>
              </button>
            ) : (
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: 14,
                  border: 'none',
                  borderRadius: 3,
                  background: theme.bgSecondary,
                  color: theme.text,
                  cursor: 'pointer',
                  outline: 'none',
                }}>
                <option value="color-cmc">Color → CMC</option>
                <option value="cmc">Mana Cost</option>
                <option value="name">Name (A-Z)</option>
                <option value="collector">Collector #</option>
                <option value="rarity">Rarity</option>
              </select>
            )}

            {/* Type filter dropdown */}
            {isMobile ? (
              <button
                onClick={() => setActiveDropdown('type')}
                style={{
                  padding: '10px 16px',
                  fontSize: 14,
                  border: 'none',
                  borderRadius: 3,
                  background: theme.bgSecondary,
                  color: theme.text,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}>
                {typeFilter === 'all' ? 'All Types' : typeFilter.charAt(0).toUpperCase() + typeFilter.slice(1) + 's'}
                <span style={{fontSize: 10}}>▼</span>
              </button>
            ) : (
              <select
                value={typeFilter}
                onChange={e => {
                  setTypeFilter(e.target.value)
                  setExpandedCard(null)
                  setStatsCard(null)
                }}
                style={{
                  padding: '8px 12px',
                  fontSize: 14,
                  border: 'none',
                  borderRadius: 3,
                  background: theme.bgSecondary,
                  color: theme.text,
                  cursor: 'pointer',
                  outline: 'none',
                }}>
                <option value="all">All Types</option>
                <option value="creature">Creatures</option>
                <option value="instant">Instants</option>
                <option value="sorcery">Sorceries</option>
                <option value="artifact">Artifacts</option>
                <option value="enchantment">Enchantments</option>
                <option value="planeswalker">Planeswalkers</option>
                <option value="land">Lands</option>
              </select>
            )}

            {/* Layout dropdown */}
            {isMobile ? (
              <button
                onClick={() => setActiveDropdown('layout')}
                style={{
                  padding: '10px 16px',
                  fontSize: 14,
                  border: 'none',
                  borderRadius: 3,
                  background: theme.bgSecondary,
                  color: theme.text,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}>
                {layout === 'stacked' ? 'Stacked' : 'Grid'}
                <span style={{fontSize: 10}}>▼</span>
              </button>
            ) : (
              <select
                value={layout}
                onChange={e => {
                  setLayout(e.target.value)
                  setExpandedCard(null)
                  setStatsCard(null)
                }}
                style={{
                  padding: '8px 12px',
                  fontSize: 14,
                  border: 'none',
                  borderRadius: 3,
                  background: theme.bgSecondary,
                  color: theme.text,
                  cursor: 'pointer',
                  outline: 'none',
                }}>
                <option value="stacked">Stacked</option>
                <option value="grid">Grid</option>
              </select>
            )}
          </div>
        )}

        {/* Mobile bottom sheet dropdowns */}
        {isMobile && activeDropdown && (
          <>
            {/* Backdrop */}
            <div
              onClick={() => setActiveDropdown(null)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.6)',
                zIndex: 9998,
                animation: 'fadeIn 0.2s ease',
              }}
            />
            {/* Bottom sheet */}
            <div
              style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                background: theme.bgSecondary,
                borderRadius: '16px 16px 0 0',
                padding: '16px 0',
                zIndex: 9999,
                animation: 'slideUp 0.25s ease',
                maxHeight: '60vh',
                overflowY: 'auto',
              }}>
              {/* Drag handle */}
              <div style={{display: 'flex', justifyContent: 'center', marginBottom: 8}}>
                <div style={{width: 40, height: 4, background: '#555', borderRadius: 2}} />
              </div>
              {/* Title */}
              <div
                style={{
                  padding: '8px 20px 16px',
                  fontSize: 16,
                  fontWeight: 500,
                  color: theme.text,
                  borderBottom: `1px solid ${theme.bg}`,
                }}>
                {activeDropdown === 'sort' ? 'Sort By' : activeDropdown === 'type' ? 'Card Type' : 'Layout'}
              </div>
              {/* Options */}
              {activeDropdown === 'sort' && (
                <>
                  {[
                    {value: 'color-cmc', label: 'Color → CMC'},
                    {value: 'cmc', label: 'Mana Cost'},
                    {value: 'name', label: 'Name (A-Z)'},
                    {value: 'collector', label: 'Collector #'},
                    {value: 'rarity', label: 'Rarity'},
                  ].map(opt => (
                    <div
                      key={opt.value}
                      onClick={() => {
                        setSortBy(opt.value)
                        setActiveDropdown(null)
                      }}
                      style={{
                        padding: '16px 20px',
                        color: sortBy === opt.value ? '#4a9eff' : theme.text,
                        fontSize: 16,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}>
                      {opt.label}
                      {sortBy === opt.value && <span style={{color: '#4a9eff'}}>✓</span>}
                    </div>
                  ))}
                </>
              )}
              {activeDropdown === 'type' && (
                <>
                  {[
                    {value: 'all', label: 'All Types'},
                    {value: 'creature', label: 'Creatures'},
                    {value: 'instant', label: 'Instants'},
                    {value: 'sorcery', label: 'Sorceries'},
                    {value: 'artifact', label: 'Artifacts'},
                    {value: 'enchantment', label: 'Enchantments'},
                    {value: 'planeswalker', label: 'Planeswalkers'},
                    {value: 'land', label: 'Lands'},
                  ].map(opt => (
                    <div
                      key={opt.value}
                      onClick={() => {
                        setTypeFilter(opt.value)
                        setExpandedCard(null)
                        setStatsCard(null)
                        setActiveDropdown(null)
                      }}
                      style={{
                        padding: '16px 20px',
                        color: typeFilter === opt.value ? '#4a9eff' : theme.text,
                        fontSize: 16,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}>
                      {opt.label}
                      {typeFilter === opt.value && <span style={{color: '#4a9eff'}}>✓</span>}
                    </div>
                  ))}
                </>
              )}
              {activeDropdown === 'layout' && (
                <>
                  {[
                    {value: 'stacked', label: 'Stacked'},
                    {value: 'grid', label: 'Grid'},
                  ].map(opt => (
                    <div
                      key={opt.value}
                      onClick={() => {
                        setLayout(opt.value)
                        setExpandedCard(null)
                        setStatsCard(null)
                        setActiveDropdown(null)
                      }}
                      style={{
                        padding: '16px 20px',
                        color: layout === opt.value ? '#4a9eff' : theme.text,
                        fontSize: 16,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}>
                      {opt.label}
                      {layout === opt.value && <span style={{color: '#4a9eff'}}>✓</span>}
                    </div>
                  ))}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Loading state */}
      {(loading || loadingSet) && (
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 40}}>
          <div
            style={{
              width: 40,
              height: 40,
              border: `3px solid ${theme.bgSecondary}`,
              borderTopColor: theme.text,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <div style={{color: theme.textSecondary, fontSize: 14, marginTop: 16}}>
            {loadingSet ? 'Loading set...' : 'Loading...'}
          </div>
        </div>
      )}

      {/* Error state */}
      {error && !loading && !loadingSet && (
        <div style={{color: '#f55', fontSize: 16, marginTop: 40, textAlign: 'center', maxWidth: 400}}>{error}</div>
      )}

      {/* Cards display */}
      {!loading && !loadingSet && !error && displayCards.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobile ? 'center' : 'stretch',
            width: '100%',
            maxWidth: layout === 'stacked' && isMobile ? cardWidth + 40 : '100%',
            // Desktop: inset content to x=86 (root padLeft 32 + 54) so the leftmost column sits under the fixed title.
            padding: isMobile ? 0 : '0 20px 0 54px',
          }}>
          {/* Stacked layout - Mobile: single column, click to expand */}
          {layout === 'stacked' && isMobile && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: cardWidth,
              }}>
              {displayCards.map((card, index) => {
                const isExpanded = expandedCard === index
                const isAfterExpanded = expandedCard !== null && index === expandedCard + 1
                const marginTop = index === 0 ? 0 : isAfterExpanded ? 0 : -(cardHeight - STACK_VISIBLE_MOBILE)

                return (
                  <div
                    key={`${card.name}-${card.setCode}-${index}`}
                    onClick={() => setExpandedCard(isExpanded ? null : index)}
                    style={{
                      marginTop,
                      transition: 'margin-top 0.3s ease, padding-bottom 0.3s ease',
                      cursor: 'pointer',
                      zIndex: isExpanded ? 10 : 1,
                      position: 'relative',
                      WebkitTapHighlightColor: 'transparent',
                      paddingBottom: isExpanded ? 28 : 0,
                    }}>
                    <CardImage card={card} width={cardWidth} height={cardHeight} />
                    {/* Price display - show when expanded */}
                    {isExpanded && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 7,
                          left: 0,
                          right: 0,
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 16,
                          fontSize: 12,
                          color: theme.textSecondary,
                        }}>
                        <span>
                          TCG:{' '}
                          <span style={{color: card.prices?.usd ? '#4ade80' : theme.textSecondary}}>
                            {card.prices?.usd ? `$${card.prices.usd}` : '--'}
                          </span>
                        </span>
                        <span>
                          CM:{' '}
                          <span style={{color: card.prices?.eur ? theme.text : theme.textSecondary}}>
                            {card.prices?.eur ? `€${card.prices.eur}` : '--'}
                          </span>
                        </span>
                      </div>
                    )}
                    {/* Stats button - only show when expanded on mobile */}
                    {isExpanded && (
                      <button
                        onClick={e => {
                          e.stopPropagation()
                          // TODO: Open stats panel from bottom
                          console.log('Open stats for:', card.name)
                        }}
                        style={{
                          position: 'absolute',
                          bottom: 32,
                          right: 12,
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          background: 'rgba(0,0,0,0.7)',
                          border: '1px solid rgba(255,255,255,0.3)',
                          color: '#fff',
                          fontSize: 18,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        title="View card stats">
                        ℹ️
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Stacked layout - Desktop: one vertical stack per color group */}
          {layout === 'stacked' &&
            !isMobile &&
            (() => {
              // Group cards by color
              const colorGroups = {
                W: {label: 'White', cards: []},
                U: {label: 'Blue', cards: []},
                B: {label: 'Black', cards: []},
                R: {label: 'Red', cards: []},
                G: {label: 'Green', cards: []},
                M: {label: 'Multi', cards: []},
                C: {label: 'Colorless', cards: []},
                L: {label: 'Land', cards: []},
              }

              displayCards.forEach((card, originalIndex) => {
                const cardWithIndex = {...card, originalIndex}
                if (card.type_line?.toLowerCase().includes('land')) {
                  colorGroups.L.cards.push(cardWithIndex)
                } else if (!card.colors || card.colors.length === 0) {
                  colorGroups.C.cards.push(cardWithIndex)
                } else if (card.colors.length > 1) {
                  colorGroups.M.cards.push(cardWithIndex)
                } else {
                  const color = card.colors[0]
                  if (colorGroups[color]) {
                    colorGroups[color].cards.push(cardWithIndex)
                  }
                }
              })

              const activeGroups = Object.entries(colorGroups).filter(([_, group]) => group.cards.length > 0)

              return (
                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    width: '100%',
                  }}>
                  {activeGroups.map(([colorKey, group]) => (
                    <div
                      key={colorKey}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: cardWidth,
                      }}>
                      {/* Color label */}
                      <div style={{color: theme.textSecondary, fontSize: 12, marginBottom: 8, textTransform: 'uppercase'}}>
                        {group.label} ({group.cards.length})
                      </div>
                      {/* Stacked cards - hover to expand on desktop */}
                      {group.cards.map((card, index) => {
                        const isExpanded = expandedCard === card.originalIndex
                        const isStatsCard = statsCard?.originalIndex === card.originalIndex
                        const prevCard = group.cards[index - 1]
                        const isAfterExpanded = prevCard && expandedCard === prevCard.originalIndex
                        const marginTop = index === 0 ? 0 : isAfterExpanded ? 0 : -(cardHeight - STACK_VISIBLE_DESKTOP)

                        return (
                          <div
                            key={`${card.name}-${card.setCode}-${card.originalIndex}`}
                            onMouseEnter={() => setExpandedCard(card.originalIndex)}
                            onMouseLeave={() => {
                              // Don't collapse if this card is the current statsCard (keeps it expanded while panel is open)
                              if (!isStatsCard) setExpandedCard(null)
                            }}
                            onClick={() => {
                              if (isStatsCard) {
                                // Clicking again closes the panel and collapses the card
                                setStatsCard(null)
                                setStatsCardSource(null)
                                setExpandedCard(null)
                              } else {
                                // Opening stats panel - lock this card as expanded
                                setStatsCard(card)
                                setStatsCardSource(null) // Not from deck
                                setExpandedCard(card.originalIndex)
                                setShowCardBack(false) // Reset to front face for new card
                              }
                            }}
                            style={{
                              marginTop,
                              transition: 'margin-top 0.3s ease, padding-bottom 0.3s ease',
                              cursor: 'pointer',
                              zIndex: isExpanded ? 10 : 1,
                              position: 'relative',
                              WebkitTapHighlightColor: 'transparent',
                              paddingBottom: isExpanded ? 24 : 0,
                            }}>
                            <CardImage card={card} width={cardWidth} height={cardHeight} />
                            {/* Price display - show when expanded */}
                            {isExpanded && (
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: 7,
                                  left: 0,
                                  right: 0,
                                  display: 'flex',
                                  justifyContent: 'center',
                                  gap: 12,
                                  fontSize: 11,
                                  color: theme.textSecondary,
                                }}>
                                <span>
                                  TCG:{' '}
                                  <span style={{color: card.prices?.usd ? '#4ade80' : theme.textSecondary}}>
                                    {card.prices?.usd ? `$${card.prices.usd}` : '--'}
                                  </span>
                                </span>
                                <span>
                                  CM:{' '}
                                  <span style={{color: card.prices?.eur ? theme.text : theme.textSecondary}}>
                                    {card.prices?.eur ? `€${card.prices.eur}` : '--'}
                                  </span>
                                </span>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              )
            })()}

          {/* Grid layout */}
          {layout === 'grid' && (
            <div
              style={{
                display: 'grid',
                // Width-responsive (no isMobile flag): 1 column when narrow, wraps as wide as the page on desktop.
                gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${cardWidth}px), 1fr))`,
                gap: 12,
                justifyItems: 'center',
                width: '100%',
                boxSizing: 'border-box',
              }}>
              {displayCards.map((card, index) => (
                <CardImage key={`${card.name}-${card.setCode}-${index}`} card={card} width={cardWidth} height={cardHeight} />
              ))}
            </div>
          )}

          {/* Load More button for search results */}
          {searchResults && searchHasMore && (
            <button
              onClick={loadMoreResults}
              disabled={loadingMore}
              style={{
                marginTop: 24,
                padding: '12px 32px',
                fontSize: 16,
                border: 'none',
                borderRadius: 3,
                background: loadingMore ? '#333' : '#444',
                color: '#fff',
                cursor: loadingMore ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
              {loadingMore ? (
                <>
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      border: '2px solid #555',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                  Loading...
                </>
              ) : (
                <>
                  <span style={{fontSize: 20, lineHeight: 1}}>+</span>
                  Load More Arts
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Empty state */}
      {!loading && !loadingSet && !error && !currentSet && !searchResults && (
        <div style={{color: theme.textSecondary, fontSize: 16, marginTop: 40}}>
          Select a set from the menu to view cards.
        </div>
      )}

      {/* Stats Panel - Desktop only, slides from right */}
      {!isMobile && statsCard && (
        <>
          {/* Full screen overlay with opacity */}
          <div
            onClick={() => {
              setStatsCard(null)
              setStatsCardSource(null)
              setExpandedCard(null)
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: activeDeck ? 699 : 399,
              cursor: isDragging ? 'ew-resize' : 'pointer',
              background: 'rgba(0, 0, 0, 0.7)',
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              width: showVariantsGrid
                ? Math.min(window.innerWidth * 0.85, 1600)
                : (showLargeCard ? largeCardWidth + 300 : 300),
              height: '100vh',
              background: theme.bg,
              borderLeft: `1px solid ${theme.border}`,
              zIndex: activeDeck ? 700 : 400,
              display: 'flex',
              boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
              animation: isDragging ? 'none' : 'slideInRight 0.3s ease',
              transition: isDragging ? 'none' : 'width 0.3s ease',
            }}>
            <style>
              {`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}
            </style>

            {/* Drag handle - left edge */}
            {showLargeCard && (
              <div
                onMouseDown={e => {
                  e.preventDefault()
                  setIsDragging(true)
                }}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: 6,
                  height: '100%',
                  cursor: 'ew-resize',
                  background: isDragging ? theme.bgTertiary : 'transparent',
                  zIndex: 10,
                  transition: 'background 0.2s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = theme.bgTertiary)}
                onMouseLeave={e => {
                  if (!isDragging) e.currentTarget.style.background = 'transparent'
                }}
              />
            )}

            {/* Variants Grid Panel - Shows when grid icon is clicked */}
            {showVariantsGrid && (
              <div
                style={{
                  flex: 1,
                  height: '100vh',
                  overflowY: 'auto',
                  background: darkMode ? '#111' : '#e5e5e5',
                  borderRight: `1px solid ${theme.border}`,
                  padding: '16px 16px 100px 16px',
                }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                }}>
                  <span style={{color: theme.text, fontSize: 14, fontWeight: 500}}>
                    All Printings {cardVariants.length > 0 ? `(${cardVariants.length})` : ''}
                  </span>
                  <button
                    onClick={() => setShowVariantsGrid(false)}
                    style={{
                      background: 'rgba(0,0,0,0.3)',
                      border: 'none',
                      color: '#fff',
                      fontSize: 16,
                      cursor: 'pointer',
                      padding: '4px 10px',
                      borderRadius: 3,
                      lineHeight: 1,
                    }}
                    title="Close grid">
                    ×
                  </button>
                </div>
                {loadingVariants ? (
                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200}}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        border: '3px solid #444',
                        borderTopColor: '#fff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }}
                    />
                  </div>
                ) : cardVariants.length === 0 ? (
                  <div style={{color: theme.textSecondary, fontSize: 13, textAlign: 'center', marginTop: 40}}>
                    No other printings found
                  </div>
                ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: 12,
                }}>
                  {cardVariants.map((variant, i) => {
                    const isCurrent =
                      variant.set === (statsCard?.setCode || statsCard?.set) &&
                      variant.collector_number === statsCard?.collector_number
                    const isLocked = lockedVariantImage === variant.image_uri

                    // Build variant label
                    let label = variant.set_name
                    if (variant.promo_types?.includes('prerelease')) label += ' Prerelease'
                    else if (variant.promo_types?.includes('stamped')) label += ' Stamp'
                    else if (variant.frame_effects?.includes('extendedart')) label += ' Extended'
                    if (variant.finishes?.includes('foil') && !variant.finishes?.includes('nonfoil')) label += ' Foil'

                    return (
                      <div
                        key={variant.id}
                        onMouseEnter={() => {
                          if (variant.image_uri) {
                            setHoveredVariantImage(variant.image_uri)
                          }
                        }}
                        onMouseLeave={() => {
                          setHoveredVariantImage(null)
                        }}
                        onClick={() => {
                          if (variant.image_uri) {
                            // If opened from deck, update the card and close panel
                            if (statsCardSource) {
                              updateDeckCardVariant(statsCardSource.stackId, statsCardSource.deckCardId, variant)
                              setStatsCard(null)
                              setStatsCardSource(null)
                              setLockedVariantImage(null)
                              setHoveredVariantImage(null)
                            } else {
                              setLockedVariantImage(isLocked ? null : variant.image_uri)
                            }
                          }
                        }}
                        style={{
                          cursor: 'pointer',
                          borderRadius: 8,
                          overflow: 'hidden',
                          border: isLocked ? '2px solid #4ade80' : isCurrent ? '2px solid #60a5fa' : '2px solid transparent',
                          transition: 'transform 0.1s ease, border-color 0.1s ease',
                        }}
                        onMouseOver={e => e.currentTarget.style.transform = 'scale(1.02)'}
                        onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}>
                        {variant.image_uri ? (
                          <img
                            src={variant.image_uri}
                            alt={label}
                            style={{
                              width: '100%',
                              display: 'block',
                              borderRadius: 6,
                            }}
                            loading="lazy"
                          />
                        ) : (
                          <div style={{
                            aspectRatio: '488/680',
                            background: theme.bgSecondary,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.textSecondary,
                            fontSize: 12,
                          }}>
                            No image
                          </div>
                        )}
                        <div style={{
                          padding: '6px 8px',
                          background: theme.bg,
                          fontSize: 10,
                          color: theme.textSecondary,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {label}
                          {variant.usd && <span style={{color: '#4ade80', marginLeft: 6}}>${variant.usd}</span>}
                        </div>
                      </div>
                    )
                  })}
                </div>
                )}
              </div>
            )}

            {/* Large Card Image */}
            {showLargeCard && (
              <div
                style={{
                  width: largeCardWidth,
                  height: '100vh',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: darkMode ? '#111' : '#e5e5e5',
                  position: 'relative',
                  borderRight: `1px solid ${theme.border}`,
                }}>
                {/* Close large card button */}
                <button
                  onClick={() => setShowLargeCard(false)}
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: 'rgba(0,0,0,0.5)',
                    border: 'none',
                    color: '#fff',
                    fontSize: 18,
                    cursor: 'pointer',
                    padding: '4px 10px',
                    borderRadius: 3,
                    lineHeight: 1,
                  }}
                  title="Hide large card">
                  ×
                </button>
                {/* Flip button for double-faced cards */}
                {statsCard.card_faces && statsCard.card_faces.length > 1 && statsCard.card_faces[1]?.image_uris && (
                  <button
                    onClick={() => setShowCardBack(!showCardBack)}
                    style={{
                      position: 'absolute',
                      top: 12,
                      left: 12,
                      background: 'rgba(0,0,0,0.5)',
                      border: 'none',
                      color: '#fff',
                      fontSize: 14,
                      cursor: 'pointer',
                      padding: '6px 12px',
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                    title={showCardBack ? 'Show front face' : 'Show back face'}>
                    <span style={{fontSize: 16}}>🔄</span>
                    {showCardBack ? 'Front' : 'Back'}
                  </button>
                )}
                {/* Card image - as large as possible while maintaining aspect ratio */}
                {/* Use hoveredVariantImage or lockedVariantImage when viewing variants */}
                <img
                  src={
                    hoveredVariantImage ||
                    lockedVariantImage ||
                    (showCardBack && statsCard.card_faces?.[1]?.image_uris?.normal
                      ? statsCard.card_faces[1].image_uris.normal
                      : statsCard.url)
                  }
                  alt={showCardBack && statsCard.card_faces?.[1]?.name ? statsCard.card_faces[1].name : statsCard.name}
                  style={{
                    width: 'auto',
                    height: 'calc(100vh - 80px)',
                    maxWidth: 'calc(100% - 40px)',
                    maxHeight: 'calc(100vh - 80px)',
                    borderRadius: 12,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                    objectFit: 'contain',
                  }}
                />
              </div>
            )}

            {/* Stats Panel - Right side */}
            <div style={{width: 300, height: '100vh', overflowY: 'auto'}}>
              {/* Header */}
              <div
                style={{
                  padding: 16,
                  borderBottom: `1px solid ${theme.border}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}>
                <div style={{flex: 1}}>
                  {/* Show large card toggle if hidden */}
                  {!showLargeCard && (
                    <button
                      onClick={() => setShowLargeCard(true)}
                      style={{
                        background: theme.bgSecondary,
                        border: 'none',
                        color: theme.text,
                        fontSize: 12,
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: 3,
                        marginBottom: 8,
                      }}>
                      🖼️ Show Card
                    </button>
                  )}
                  <div style={{color: theme.text, fontSize: 16, fontWeight: 500}}>{statsCard.name}</div>
                  <div style={{color: theme.textSecondary, fontSize: 12, marginTop: 4}}>
                    <span
                      onClick={() => {
                        // Load this set and close stats panel
                        const setCode = statsCard.setCode || statsCard.set
                        if (setCode) {
                          loadSet(setCode, statsCard.setName)
                          setStatsCard(null)
                          setStatsCardSource(null)
                          setExpandedCard(null)
                          clearSearch()
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        textDecorationStyle: 'dotted',
                        textUnderlineOffset: 2,
                      }}
                      title={`View ${statsCard.setName}`}
                    >
                      {statsCard.setName}
                    </span>
                    {' • '}{statsCard.rarity}
                  </div>
                </div>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4}}>
                  <button
                    onClick={() => {
                      setStatsCard(null)
                      setStatsCardSource(null)
                      setExpandedCard(null)
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: theme.textSecondary,
                      fontSize: 24,
                      cursor: 'pointer',
                      padding: '0 4px',
                      lineHeight: 1,
                    }}>
                    ×
                  </button>
                  {/* Refresh price button */}
                  <button
                    onClick={manualRefreshPrice}
                    disabled={refreshingPrice}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: theme.textSecondary,
                      fontSize: 14,
                      cursor: refreshingPrice ? 'default' : 'pointer',
                      padding: '4px',
                      lineHeight: 1,
                      opacity: refreshingPrice ? 1 : 0.6,
                    }}
                    title="Refresh prices">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        animation: refreshingPrice ? 'spin 1s linear infinite' : 'none',
                      }}>
                      <path d="M21 2v6h-6" />
                      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                      <path d="M3 22v-6h6" />
                      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Market Prices */}
              <div style={{padding: 16, borderBottom: `1px solid ${theme.border}`}}>
                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: theme.textSecondary, fontSize: 13}}>TCGPlayer</span>
                    <span
                      style={{
                        color: statsCard.prices?.usd ? '#4ade80' : theme.text,
                        fontSize: 13,
                        fontWeight: statsCard.prices?.usd ? 500 : 400,
                      }}>
                      {statsCard.prices?.usd ? `$${statsCard.prices.usd}` : '--'}
                    </span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: theme.textSecondary, fontSize: 13}}>TCGPlayer (Foil)</span>
                    <span style={{color: statsCard.prices?.usd_foil ? '#c084fc' : theme.text, fontSize: 13}}>
                      {statsCard.prices?.usd_foil ? `$${statsCard.prices.usd_foil}` : '--'}
                    </span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: theme.textSecondary, fontSize: 13}}>Cardmarket (EUR)</span>
                    <span style={{color: theme.text, fontSize: 13}}>
                      {statsCard.prices?.eur ? `€${statsCard.prices.eur}` : '--'}
                    </span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: theme.textSecondary, fontSize: 13}}>MTGO (tix)</span>
                    <span style={{color: theme.text, fontSize: 13}}>
                      {statsCard.prices?.tix ? `${statsCard.prices.tix} tix` : '--'}
                    </span>
                  </div>
                </div>
              </div>


              {/* Card Details */}
              <div style={{padding: 16, borderBottom: `1px solid ${theme.border}`}}>
                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: theme.textSecondary, fontSize: 13}}>Mana Cost</span>
                    <span style={{color: theme.text, fontSize: 13}}>{statsCard.mana_cost || 'N/A'}</span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: theme.textSecondary, fontSize: 13}}>CMC</span>
                    <span style={{color: theme.text, fontSize: 13}}>{statsCard.cmc ?? 'N/A'}</span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: theme.textSecondary, fontSize: 13}}>Type</span>
                    <span style={{color: theme.text, fontSize: 13, textAlign: 'right', maxWidth: 160}}>
                      {statsCard.type_line || 'N/A'}
                    </span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: theme.textSecondary, fontSize: 13}}>Collector #</span>
                    <span style={{color: theme.text, fontSize: 13}}>{statsCard.collector_number || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* EDHREC Stats */}
              <div style={{padding: 16, borderBottom: `1px solid ${theme.border}`}}>
                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: theme.textSecondary, fontSize: 13}}>Decks</span>
                    <span style={{color: theme.text, fontSize: 13}}>--</span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: theme.textSecondary, fontSize: 13}}>% of Decks</span>
                    <span style={{color: theme.text, fontSize: 13}}>--%</span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: theme.textSecondary, fontSize: 13}}>Rank</span>
                    <span style={{color: theme.text, fontSize: 13}}>#--</span>
                  </div>
                </div>
              </div>

              {/* Format Legality */}
              <div style={{padding: 16, borderBottom: `1px solid ${theme.border}`}}>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                  {['Standard', 'Pioneer', 'Modern', 'Legacy', 'Vintage', 'Commander', 'Pauper'].map(format => (
                    <span
                      key={format}
                      style={{
                        padding: '4px 8px',
                        borderRadius: 3,
                        background: theme.bgSecondary,
                        color: theme.textSecondary,
                        fontSize: 11,
                      }}>
                      {format}: --
                    </span>
                  ))}
                </div>
              </div>

              {/* Card Variants / Other Printings */}
              <div style={{padding: 16}}>
                <div
                  style={{
                    color: theme.textSecondary,
                    fontSize: 11,
                    marginBottom: 8,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}>
                  <span>Other Printings</span>
                  {cardVariants.length > 0 && (
                    <button
                      onClick={() => setShowVariantsGrid(!showVariantsGrid)}
                      style={{
                        background: showVariantsGrid ? theme.bgSecondary : 'transparent',
                        border: 'none',
                        color: theme.textSecondary,
                        cursor: 'pointer',
                        padding: 4,
                        borderRadius: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title={showVariantsGrid ? 'Hide image grid' : 'Show image grid'}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <rect x="3" y="3" width="7" height="7" rx="1" />
                        <rect x="14" y="3" width="7" height="7" rx="1" />
                        <rect x="3" y="14" width="7" height="7" rx="1" />
                        <rect x="14" y="14" width="7" height="7" rx="1" />
                      </svg>
                    </button>
                  )}
                </div>
                {loadingVariants ? (
                  <div style={{color: theme.textSecondary, fontSize: 12, display: 'flex', alignItems: 'center', gap: 8}}>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{animation: 'spin 1s linear infinite'}}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Loading variants...
                  </div>
                ) : cardVariants.length === 0 ? (
                  <div style={{color: theme.textSecondary, fontSize: 12}}>No other printings found</div>
                ) : (
                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    {cardVariants.map((variant, i) => {
                      // Build variant label
                      let label = variant.set_name
                      if (variant.promo_types?.includes('prerelease')) label += ' Prerelease'
                      else if (variant.promo_types?.includes('stamped')) label += ' Stamp'
                      else if (variant.frame_effects?.includes('extendedart')) label += ' Extended'
                      if (variant.finishes?.includes('foil') && !variant.finishes?.includes('nonfoil')) label += ' Foil'

                      // Highlight current card
                      const isCurrent =
                        variant.set === (statsCard?.setCode || statsCard?.set) &&
                        variant.collector_number === statsCard?.collector_number

                      const isLocked = lockedVariantImage === variant.image_uri

                      return (
                        <div
                          key={variant.id}
                          onMouseEnter={() => {
                            if (variant.image_uri) {
                              setHoveredVariantImage(variant.image_uri)
                              // Trigger background cache on our server only if not already cached
                              if (variant.cache_url && !variant.is_cached) {
                                const img = new Image()
                                img.src = variant.cache_url
                              }
                            }
                          }}
                          onMouseLeave={() => {
                            setHoveredVariantImage(null)
                          }}
                          onClick={() => {
                            if (variant.image_uri) {
                              // Toggle lock: if already locked, unlock; otherwise lock this one
                              setLockedVariantImage(isLocked ? null : variant.image_uri)
                              // If opened from deck, also update the card's art
                              if (statsCardSource && !isLocked) {
                                updateDeckCardVariant(statsCardSource.stackId, statsCardSource.deckCardId, variant)
                              }
                            }
                          }}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 4px',
                            opacity: isCurrent ? 1 : 0.8,
                            cursor: variant.image_uri ? 'pointer' : 'default',
                            borderRadius: 3,
                            marginLeft: -4,
                            marginRight: -4,
                            transition: 'background 0.1s ease',
                            background: isLocked || hoveredVariantImage === variant.image_uri ? theme.bgSecondary : 'transparent',
                          }}
                          onMouseOver={e => {
                            if (variant.image_uri) e.currentTarget.style.background = theme.bgSecondary
                          }}
                          onMouseOut={e => {
                            // Keep background if this variant is locked
                            if (lockedVariantImage !== variant.image_uri) {
                              e.currentTarget.style.background = 'transparent'
                            }
                          }}>
                          <span
                            style={{
                              color: isCurrent ? theme.text : theme.textSecondary,
                              fontSize: 11,
                              fontWeight: isCurrent ? 500 : 400,
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              marginRight: 8,
                            }}>
                            {label}
                            {isCurrent && <span style={{color: '#4ade80', marginLeft: 4}}>●</span>}
                          </span>
                          <div style={{display: 'flex', gap: 12, fontSize: 11, flexShrink: 0}}>
                            <span
                              style={{
                                color: variant.usd ? '#4ade80' : theme.textSecondary,
                                minWidth: 45,
                                textAlign: 'right',
                              }}>
                              {variant.usd ? `$${variant.usd}` : '--'}
                            </span>
                            <span
                              style={{
                                color: variant.tix ? theme.text : theme.textSecondary,
                                minWidth: 45,
                                textAlign: 'right',
                              }}>
                              {variant.tix ? `${variant.tix} tix` : '--'}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                    {/* Load More button */}
                    {allVariants.length > cardVariants.length && (
                      <button
                        onClick={loadMoreVariants}
                        disabled={loadingMoreVariants}
                        style={{
                          marginTop: 8,
                          padding: '6px 12px',
                          background: 'transparent',
                          border: `1px solid ${theme.textSecondary}`,
                          borderRadius: 4,
                          color: theme.textSecondary,
                          fontSize: 11,
                          cursor: loadingMoreVariants ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                          width: '100%',
                          opacity: loadingMoreVariants ? 0.6 : 1,
                        }}
                        onMouseOver={e => {
                          if (!loadingMoreVariants) {
                            e.currentTarget.style.background = theme.bgSecondary
                            e.currentTarget.style.borderColor = theme.text
                            e.currentTarget.style.color = theme.text
                          }
                        }}
                        onMouseOut={e => {
                          e.currentTarget.style.background = 'transparent'
                          e.currentTarget.style.borderColor = theme.textSecondary
                          e.currentTarget.style.color = theme.textSecondary
                        }}>
                        {loadingMoreVariants ? (
                          <>
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              style={{animation: 'spin 1s linear infinite'}}>
                              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                            </svg>
                            Loading...
                          </>
                        ) : (
                          `+ Load More (${allVariants.length - cardVariants.length} remaining)`
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Import Panel - slides from right */}
      {showImportPanel && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => {
              if (!importing) {
                setShowImportPanel(false)
                setImportText('')
                setImportProgress({current: 0, total: 0, status: ''})
              }
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 700,
            }}
          />
          {/* Panel */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              width: isMobile ? '90vw' : '50vw',
              height: '100vh',
              background: theme.bg,
              zIndex: 800,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
              animation: 'slideInFromRight 0.2s ease-out',
            }}>
            {/* Header */}
            <div
              style={{
                padding: 16,
                borderBottom: `1px solid ${theme.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <span style={{color: theme.text, fontSize: 18, fontWeight: 500}}>Import Deck List</span>
              <button
                onClick={() => {
                  if (!importing) {
                    setShowImportPanel(false)
                    setImportText('')
                    setImportProgress({current: 0, total: 0, status: ''})
                  }
                }}
                disabled={importing}
                style={{
                  background: 'none',
                  border: 'none',
                  color: importing ? theme.border : theme.textSecondary,
                  fontSize: 24,
                  cursor: importing ? 'not-allowed' : 'pointer',
                  padding: '0 8px',
                }}>
                ×
              </button>
            </div>

            {/* Instructions */}
            <div style={{padding: '12px 16px', borderBottom: `1px solid ${theme.border}`}}>
              <p style={{color: theme.textSecondary, fontSize: 13, margin: 0, lineHeight: 1.5}}>
                Paste your deck list below. Format:{' '}
                <code style={{background: theme.bgSecondary, padding: '2px 4px', borderRadius: 3}}>1 Card Name</code> per
                line.
                <br />
                Max 1000 cards.
              </p>
            </div>

            {/* Textarea */}
            <div style={{flex: 1, padding: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
              <textarea
                value={importText}
                onChange={e => setImportText(e.target.value)}
                placeholder={`1 Lightning Bolt\n4 Counterspell\n2 Black Lotus\n...`}
                disabled={importing}
                spellCheck={false}
                style={{
                  flex: 1,
                  width: '100%',
                  padding: 12,
                  fontSize: 14,
                  fontFamily: 'monospace',
                  border: `1px solid ${theme.border}`,
                  borderRadius: 4,
                  background: theme.bgSecondary,
                  color: theme.text,
                  outline: 'none',
                  resize: 'none',
                  opacity: importing ? 0.6 : 1,
                }}
              />
            </div>

            {/* Progress / Status */}
            {(importing || importProgress.status) && (
              <div style={{padding: '12px 16px', borderTop: `1px solid ${theme.border}`}}>
                {importing && importProgress.total > 0 && (
                  <div style={{marginBottom: 8}}>
                    <div
                      style={{
                        height: 6,
                        background: theme.bgSecondary,
                        borderRadius: 3,
                        overflow: 'hidden',
                      }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${(importProgress.current / importProgress.total) * 100}%`,
                          background: '#4ade80',
                          transition: 'width 0.2s',
                        }}
                      />
                    </div>
                    <div style={{color: theme.textSecondary, fontSize: 12, marginTop: 4}}>
                      {importProgress.current} / {importProgress.total}
                    </div>
                  </div>
                )}
                <div style={{color: theme.text, fontSize: 13}}>{importProgress.status}</div>
              </div>
            )}

            {/* Footer with Submit */}
            <div
              style={{
                padding: 16,
                borderTop: `1px solid ${theme.border}`,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12,
              }}>
              <button
                onClick={() => {
                  setShowImportPanel(false)
                  setImportText('')
                  setImportProgress({current: 0, total: 0, status: ''})
                }}
                disabled={importing}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  border: `1px solid ${theme.border}`,
                  borderRadius: 4,
                  color: theme.textSecondary,
                  fontSize: 14,
                  cursor: importing ? 'not-allowed' : 'pointer',
                  opacity: importing ? 0.5 : 1,
                }}>
                Cancel
              </button>
              <button
                onClick={importDeckList}
                disabled={importing || !importText.trim()}
                style={{
                  padding: '10px 24px',
                  background: importing || !importText.trim() ? '#2d5a3d' : '#22c55e',
                  border: 'none',
                  borderRadius: 4,
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: importing || !importText.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}>
                {importing ? (
                  <>
                    <span style={{animation: 'spin 1s linear infinite', display: 'inline-block'}}>⏳</span>
                    Importing...
                  </>
                ) : (
                  'Import'
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* User Profile / Decks Sidebar - slides from right */}
      {userMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setUserMenuOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 900,
            }}
          />
          {/* Sidebar */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              width: isMobile ? '94vw' : '88vw',
              height: '100vh',
              background: theme.bg,
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
              animation: 'slideInFromRight 0.2s ease-out',
            }}>
            {/* Header */}
            <div
              style={{
                padding: '4px 12px 10px',
                borderBottom: `1px solid ${theme.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <span style={{color: theme.text, fontSize: 18, fontWeight: 500}}>My Decks</span>
                <button className="blueHover" onClick={createNewDeck} style={{...iconBtn, fontSize: 18, lineHeight: 1}} title="New deck">+</button>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                <button
                  onClick={() => {
                    setDeckRearrangeMode(prev => !prev)
                    setDraggingDeckId(null)
                    setDragOverDeckId(null)
                    setDragOverDeckPosition('before')
                  }}
                  className="blueHover"
                  style={{...iconBtn, ...(deckRearrangeMode ? {borderColor: '#4a9eff', color: '#4a9eff'} : {})}}
                  title="Rearrange decks (H)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                  </svg>
                </button>
                {/* Dark/Light mode toggle */}
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className="blueHover"
                  style={{...iconBtn, fontSize: 14}}
                  title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
                  {darkMode ? '☀' : '☾'}
                </button>
                {currentUser && (
                  <button
                    onClick={handleLogout}
                    className="blueHover"
                    style={iconBtn}
                    title="Log out">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                      <line x1="12" y1="2" x2="12" y2="12" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => setUserMenuOpen(false)}
                  className="blueHover"
                  style={iconBtn}
                  title="Close">
                  ×
                </button>
              </div>
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div style={{padding: 16, borderBottom: `1px solid ${theme.border}`, background: theme.bgSecondary}}>
                <div style={{color: theme.text, fontSize: 14, fontWeight: 500, marginBottom: 12}}>Settings</div>
                <div style={{marginBottom: 8}}>
                  <label style={{color: theme.textSecondary, fontSize: 12, display: 'block', marginBottom: 4}}>
                    OpenAI API Key
                  </label>
                  <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                    <input
                      type="text"
                      value={userApiKey}
                      onChange={e => {
                        setUserApiKey(e.target.value)
                        setApiKeySaved(false)
                      }}
                      onFocus={e => e.target.select()}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        fontSize: 13,
                        border: 'none',
                        borderRadius: 4,
                        background: theme.bg,
                        color: theme.text,
                        outline: 'none',
                        fontFamily: 'Lato, sans-serif',
                        fontWeight: 300,
                      }}
                    />
                    <button
                      onClick={async () => {
                        if (!userApiKey.trim() || userApiKey === apiKeyPreview) return
                        setSavingApiKey(true)
                        try {
                          const res = await Axios.post('/api/save-api-key', {apiKey: userApiKey})
                          console.log('[Settings] API key saved:', res.data)
                          setApiKeySaved(true)
                          // Update preview to new key
                          const newPreview = userApiKey.slice(0, 5) + '...' + userApiKey.slice(-4)
                          setApiKeyPreview(newPreview)
                          setUserApiKey(newPreview)
                          setTimeout(() => setApiKeySaved(false), 2000)
                        } catch (err) {
                          console.error('[Settings] Error saving API key:', err)
                        } finally {
                          setSavingApiKey(false)
                        }
                      }}
                      disabled={savingApiKey || !userApiKey.trim() || userApiKey === apiKeyPreview}
                      style={{
                        padding: '8px 16px',
                        background: theme.bg,
                        border: 'none',
                        borderRadius: 4,
                        color: apiKeySaved ? '#4ade80' : theme.text,
                        fontSize: 13,
                        fontWeight: 300,
                        cursor: savingApiKey || !userApiKey.trim() || userApiKey === apiKeyPreview ? 'not-allowed' : 'pointer',
                        opacity: savingApiKey || !userApiKey.trim() || userApiKey === apiKeyPreview ? 0.5 : 1,
                        minWidth: 50,
                      }}>
                      {apiKeySaved ? '✓' : savingApiKey ? '...' : 'save'}
                    </button>
                  </div>
                  <div style={{color: theme.textSecondary, fontSize: 11, marginTop: 6}}>
                    api key is encrypted
                  </div>
                </div>
              </div>
            )}


            {/* Deck Grid */}
            <div style={{flex: 1, overflowY: 'auto', padding: 16}}>
              {decks.length === 0 ? (
                <div style={{padding: 24, textAlign: 'center', color: theme.textSecondary}}>
                  No decks yet. Create one to get started!
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile
                      ? 'repeat(auto-fill, minmax(132px, 1fr))'
                      : 'repeat(auto-fill, minmax(160px, 180px))',
                    gap: isMobile ? 12 : 16,
                    alignItems: 'start',
                    justifyContent: 'start',
                  }}>
                  {decks.map(deck => {
                    const coverUrl = getDeckCoverUrl(deck)
                    const isDraggingDeck = draggingDeckId === deck.id
                    const isDragTarget = deckRearrangeMode && dragOverDeckId === deck.id && draggingDeckId !== deck.id

                    return (
                      <div
                        key={deck.id}
                        draggable={deckRearrangeMode}
                        onDragStart={e => {
                          if (!deckRearrangeMode) return
                          setDraggingDeckId(deck.id)
                          e.dataTransfer.effectAllowed = 'move'
                          e.dataTransfer.setData('text/plain', deck.id)
                        }}
                        onDragOver={e => {
                          if (!deckRearrangeMode) return
                          e.preventDefault()
                          e.dataTransfer.dropEffect = 'move'
                          const rect = e.currentTarget.getBoundingClientRect()
                          setDragOverDeckId(deck.id)
                          setDragOverDeckPosition(e.clientX > rect.left + rect.width / 2 ? 'after' : 'before')
                        }}
                        onDragLeave={e => {
                          if (!deckRearrangeMode) return
                          if (!e.currentTarget.contains(e.relatedTarget)) {
                            setDragOverDeckId(null)
                            setDragOverDeckPosition('before')
                          }
                        }}
                        onDrop={e => {
                          if (!deckRearrangeMode) return
                          e.preventDefault()
                          const fromDeckId = draggingDeckId || e.dataTransfer.getData('text/plain')
                          const rect = e.currentTarget.getBoundingClientRect()
                          const dropPosition = e.clientX > rect.left + rect.width / 2 ? 'after' : 'before'
                          reorderDecks(fromDeckId, deck.id, dropPosition)
                          setDraggingDeckId(null)
                          setDragOverDeckId(null)
                          setDragOverDeckPosition('before')
                        }}
                        onDragEnd={() => {
                          setDraggingDeckId(null)
                          setDragOverDeckId(null)
                          setDragOverDeckPosition('before')
                        }}
                        onClick={() => {
                          if (deckRearrangeMode) return
                          openDeck(deck)
                        }}
                        title={deck.name}
                        style={{
                          position: 'relative',
                          background: theme.bgSecondary,
                          border: isDragTarget ? '1px solid #4a9eff' : `1px solid ${theme.border}`,
                          borderRadius: 6,
                          overflow: 'hidden',
                          cursor: deckRearrangeMode ? 'grab' : 'pointer',
                          opacity: isDraggingDeck ? 0.45 : 1,
                          transform: isDragTarget ? 'translateY(-2px)' : 'none',
                          boxShadow: isDragTarget
                            ? `${dragOverDeckPosition === 'after' ? 'inset -4px 0 0 #4a9eff' : 'inset 4px 0 0 #4a9eff'}, 0 8px 20px rgba(74, 158, 255, 0.25)`
                            : 'none',
                          transition: 'transform 0.15s, box-shadow 0.15s, opacity 0.15s, border-color 0.15s',
                        }}>
                        {coverUrl ? (
                          <div style={{aspectRatio: '488 / 680', background: theme.bg, overflow: 'hidden'}}>
                            <img
                              src={coverUrl}
                              alt=""
                              draggable={false}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            style={{
                              aspectRatio: '488 / 680',
                              background: theme.bg,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: theme.textSecondary,
                            }}>
                            <svg width="42" height="58" viewBox="0 0 42 58" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="5" y="4" width="32" height="50" rx="3" />
                              <path d="M12 17h18M12 24h18M12 31h12" />
                            </svg>
                          </div>
                        )}
                        {deckRearrangeMode && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 6,
                              left: 6,
                              width: 28,
                              height: 28,
                              borderRadius: 4,
                              background: 'rgba(0,0,0,0.55)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              pointerEvents: 'none',
                            }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                              <path d="M8 5h8M8 12h8M8 19h8" />
                            </svg>
                          </div>
                        )}
                        <button
                          onClick={e => {
                            e.stopPropagation()
                            deleteDeck(deck.id)
                          }}
                          style={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            width: 28,
                            height: 28,
                            background: 'rgba(0,0,0,0.55)',
                            border: 'none',
                            borderRadius: 4,
                            color: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                          }}
                          title="Delete deck">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18" />
                            <path d="M8 6V4h8v2" />
                            <path d="M6 6l1 15h10l1-15" />
                            <path d="M10 11v6M14 11v6" />
                          </svg>
                        </button>
                        <div style={{padding: '10px 10px 12px', minHeight: 58}}>
                          <div
                            style={{
                              color: theme.text,
                              fontSize: 13,
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                            {deck.name}
                          </div>
                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, color: theme.textSecondary, fontSize: 12, marginTop: 4}}>
                            <span>{getDeckCardCount(deck)} cards</span>
                            {(() => { let p = getDeckPrice(deck); return p > 0 ? <span style={{color: '#4ade80', fontWeight: 500}}>${p.toFixed(2)}</span> : null })()}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Deck Builder - Full screen overlay when editing a deck */}
      {activeDeck && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: theme.bg,
            zIndex: 600,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}>
          {/* Export toast notification */}
          {showExportToast && (
            <div
              style={{
                position: 'fixed',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#4ade80',
                color: '#000',
                padding: '10px 20px',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 500,
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                animation: 'slideDown 0.3s ease',
              }}>
              Copied to clipboard
            </div>
          )}
          <style>
            {`@keyframes slideDown { from { transform: translateX(-50%) translateY(-100%); opacity: 0; } to { transform: translateX(-50%) translateY(0); opacity: 1; } }`}
          </style>
          {/* Deck Builder Header */}
          <div
            style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexShrink: 0,
            }}>
            <button
              onClick={closeDeckBuilder}
              style={{
                background: theme.bgSecondary,
                border: 'none',
                borderRadius: 4,
                color: theme.text,
                fontSize: 20,
                cursor: 'pointer',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              ←
            </button>
            {editingDeckName ? (
              <input
                type="text"
                value={activeDeck.name}
                onChange={e => renameDeck(activeDeck.id, e.target.value)}
                onBlur={() => setEditingDeckName(false)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    setEditingDeckName(false)
                  }
                }}
                onFocus={e => e.target.select()}
                autoFocus
                spellCheck={false}
                style={{
                  maxWidth: 400,
                  minWidth: 115,
                  padding: '8px 12px',
                  fontSize: 16,
                  fontWeight: 500,
                  border: 'none',
                  borderRadius: 4,
                  background: theme.bgSecondary,
                  color: theme.text,
                  outline: 'none',
                }}
              />
            ) : (
              <span
                onClick={() => setEditingDeckName(true)}
                style={{
                  maxWidth: 400,
                  minWidth: 115,
                  fontSize: 16,
                  fontWeight: 500,
                  color: theme.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  padding: '8px 12px',
                  background: theme.bgSecondary,
                  borderRadius: 4,
                  cursor: 'text',
                }}
                title="Click to rename">
                {activeDeck.name}
              </span>
            )}
            <span style={{color: theme.textSecondary, fontSize: 14}}>
              {activeDeck.stacks.reduce((sum, s) => sum + s.cards.length, 0) + (activeDeck.commander ? 1 : 0)} cards
            </span>
            {/* Deck total value */}
            {(() => {
              let total = 0
              let hasPrice = false
              // Sum all cards in stacks
              activeDeck.stacks.forEach(s => {
                s.cards.forEach(c => {
                  if (c.prices?.usd) {
                    total += parseFloat(c.prices.usd)
                    hasPrice = true
                  }
                })
              })
              // Add commander price
              if (activeDeck.commander?.prices?.usd) {
                total += parseFloat(activeDeck.commander.prices.usd)
                hasPrice = true
              }
              return hasPrice ? (
                <span style={{color: '#4ade80', fontSize: 14, fontWeight: 500, marginRight: 8}}>${total.toFixed(2)}</span>
              ) : null
            })()}
            {/* Optimize button */}
            <button
              onClick={optimizeDeckPrices}
              disabled={optimizing}
              style={{
                background: optimizing ? theme.bg : theme.bgSecondary,
                border: 'none',
                borderRadius: 4,
                padding: '6px 12px',
                color: optimizing ? '#4ade80' : theme.text,
                fontSize: 13,
                cursor: optimizing ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
                minWidth: optimizing ? 140 : 'auto',
              }}
              title="Find cheapest variants for all cards">
              {optimizing ? (
                <>
                  <div style={{
                    width: 60,
                    height: 4,
                    background: theme.border,
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${(optimizeProgress.current / optimizeProgress.total) * 100}%`,
                      height: '100%',
                      background: '#4ade80',
                      transition: 'width 0.2s ease',
                    }} />
                  </div>
                  <span style={{fontSize: 11}}>
                    {optimizeProgress.current === optimizeProgress.total && optimizeProgress.saved > 0
                      ? `-$${optimizeProgress.saved.toFixed(2)}`
                      : `${optimizeProgress.current}/${optimizeProgress.total}`}
                  </span>
                </>
              ) : (
                'Optimize'
              )}
            </button>
            {/* Import button */}
            <button
              onClick={() => setShowImportPanel(true)}
              style={{
                background: theme.bgSecondary,
                border: 'none',
                borderRadius: 4,
                padding: '6px 12px',
                color: theme.text,
                fontSize: 13,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                flexShrink: 0,
              }}
              title="Import deck list">
              <span style={{fontSize: 14}}>+</span> Import
            </button>
            {/* Export button */}
            <button
              onClick={exportDeckToClipboard}
              style={{
                background: theme.bgSecondary,
                border: 'none',
                borderRadius: 4,
                padding: '6px 12px',
                color: theme.text,
                fontSize: 13,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                flexShrink: 0,
              }}
              title="Copy deck list to clipboard">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              Export
            </button>
            {/* Stack sort order dropdown */}
            <select
              value={stackSortOrder}
              onChange={e => {
                const newOrder = e.target.value
                setStackSortOrder(newOrder)
                // Save to deck
                const updatedDeck = {...activeDeck, sortOrder: newOrder}
                setActiveDeck(updatedDeck)
                setDecks(decks.map(d => d.id === activeDeck.id ? updatedDeck : d))
              }}
              style={{
                background: theme.bgSecondary,
                border: 'none',
                borderRadius: 4,
                padding: '6px 10px',
                color: theme.text,
                fontSize: 13,
                cursor: 'pointer',
                outline: 'none',
              }}>
              <option value="custom">Sort by Custom</option>
              <option value="cmc">Sort by CMC</option>
              <option value="price">Sort by Price</option>
            </select>
            {/* Spacer to push user button to the right */}
            <div style={{flex: 1}} />
            {/* Card size +/- buttons */}
            <div style={{display: 'flex', alignItems: 'center', gap: 4, marginRight: 8}}>
              <button
                onClick={() => {
                  const newScale = Math.max(0.6, deckCardScale - 0.1)
                  setDeckCardScale(newScale)
                  localStorage.setItem('deckCardScale', newScale.toString())
                }}
                style={{
                  background: theme.bgSecondary,
                  border: 'none',
                  borderRadius: 4,
                  width: 28,
                  height: 28,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.textSecondary,
                  fontSize: 18,
                }}
                title="Decrease card size">
                −
              </button>
              <button
                onClick={() => {
                  const newScale = Math.min(1.4, deckCardScale + 0.1)
                  setDeckCardScale(newScale)
                  localStorage.setItem('deckCardScale', newScale.toString())
                }}
                style={{
                  background: theme.bgSecondary,
                  border: 'none',
                  borderRadius: 4,
                  width: 28,
                  height: 28,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.textSecondary,
                  fontSize: 18,
                }}
                title="Increase card size">
                +
              </button>
            </div>
            {/* User profile button - switch decks */}
            <button
              onClick={() => setUserMenuOpen(true)}
              style={{
                background: theme.bgSecondary,
                border: 'none',
                borderRadius: 4,
                width: 40,
                height: 40,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
              title="Switch deck">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="1.5">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" />
              </svg>
            </button>
          </div>

          {/* Search Bar */}
          <div
            style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex',
              gap: 8,
              flexShrink: 0,
            }}>
            <input
              type="text"
              value={deckSearchQuery}
              onChange={e => setDeckSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchForDeck()}
              onFocus={e => e.target.select()}
              placeholder="Search cards to add..."
              spellCheck={false}
              style={{
                flex: 1,
                padding: '10px 14px',
                fontSize: 14,
                border: 'none',
                borderRadius: 4,
                background: theme.bgSecondary,
                color: theme.text,
                outline: 'none',
              }}
            />
            <button
              onClick={searchForDeck}
              disabled={deckSearching}
              style={{
                padding: 10,
                background: theme.bgSecondary,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                opacity: deckSearching ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              {deckSearching ? (
                <div
                  style={{
                    width: 20,
                    height: 20,
                    border: '2px solid #444',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.text} strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              )}
            </button>
            {/* AI Question button */}
            <button
              onClick={() => setShowAiQuestion(!showAiQuestion)}
              style={{
                width: 40,
                height: 40,
                background: showAiQuestion ? theme.bgTertiary : theme.bgSecondary,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.text,
                fontSize: 18,
                fontWeight: 600,
              }}
              title="Ask AI a question">
              ?
            </button>
          </div>

          {/* AI Question Panel */}
          {showAiQuestion && (
            <div
              style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${theme.border}`,
                display: 'flex',
                gap: 8,
                flexShrink: 0,
              }}>
              {/* Show API key input if no key, otherwise show question textarea */}
              {hasApiKey === null ? (
                <div style={{flex: 1, color: theme.textSecondary, fontSize: 13}}>Checking API key...</div>
              ) : hasApiKey === false ? (
                <>
                  <input
                    type="text"
                    value={aiPanelApiKey}
                    onChange={e => setAiPanelApiKey(e.target.value)}
                    onFocus={e => e.target.select()}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        saveAiPanelApiKey()
                      }
                    }}
                    placeholder="Enter OpenAI API key..."
                    style={{
                      flex: 1,
                      padding: '10px 14px',
                      fontSize: 14,
                      border: 'none',
                      borderRadius: 4,
                      background: theme.bgSecondary,
                      color: theme.text,
                      outline: 'none',
                      fontFamily: 'inherit',
                    }}
                  />
                  <button
                    onClick={saveAiPanelApiKey}
                    disabled={aiPanelSaving || !aiPanelApiKey.trim()}
                    style={{
                      padding: '10px 16px',
                      background: theme.bgSecondary,
                      border: 'none',
                      borderRadius: 4,
                      cursor: aiPanelSaving || !aiPanelApiKey.trim() ? 'not-allowed' : 'pointer',
                      opacity: aiPanelSaving || !aiPanelApiKey.trim() ? 0.5 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.text,
                      fontSize: 13,
                      minWidth: 50,
                    }}>
                    {aiPanelSaved ? '✓' : aiPanelSaving ? '...' : 'Save'}
                  </button>
                </>
              ) : (
                <>
                  <textarea
                    ref={aiQuestionRef}
                    value={aiQuestion}
                    onChange={e => setAiQuestion(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        submitAiQuestion()
                      }
                    }}
                    placeholder="Ask a question about your deck..."
                    spellCheck={false}
                    rows={3}
                    style={{
                      flex: 1,
                      padding: '10px 14px',
                      fontSize: 14,
                      border: 'none',
                      borderRadius: 4,
                      background: theme.bgSecondary,
                      color: theme.text,
                      outline: 'none',
                      resize: 'none',
                      fontFamily: 'inherit',
                    }}
                  />
                  <div style={{display: 'flex', flexDirection: 'column', gap: 6, alignSelf: 'flex-end'}}>
                    <select
                      value={aiReasoningEffort}
                      onChange={e => setAiReasoningEffort(e.target.value)}
                      style={{
                        padding: '6px 8px',
                        background: theme.bgSecondary,
                        border: `1px solid ${theme.border}`,
                        borderRadius: 4,
                        color: theme.text,
                        fontSize: 11,
                        cursor: 'pointer',
                        outline: 'none',
                      }}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                    <button
                      onClick={submitAiQuestion}
                      disabled={aiAsking || !aiQuestion.trim()}
                      style={{
                        padding: '10px 16px',
                        background: theme.bgSecondary,
                        border: 'none',
                        borderRadius: 4,
                        cursor: aiAsking || !aiQuestion.trim() ? 'not-allowed' : 'pointer',
                        opacity: aiAsking || !aiQuestion.trim() ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.text,
                        fontSize: 13,
                      }}>
                      {aiAsking ? 'Asking...' : 'Ask'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* AI Loading State */}
          {aiAsking && (
            <div
              style={{
                padding: '16px',
                margin: '0 16px 12px 16px',
                borderRadius: 4,
                background: theme.bgSecondary,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexShrink: 0,
              }}>
              <div
                style={{
                  width: 20,
                  height: 20,
                  border: `2px solid ${theme.border}`,
                  borderTopColor: theme.text,
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <span style={{color: theme.textSecondary, fontSize: 13}}>thinking...</span>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* AI Error Display */}
          {!aiAsking && aiResponse?.error && (
            <div
              style={{
                padding: '12px 16px',
                margin: '0 16px 12px 16px',
                borderRadius: 4,
                background: theme.bgSecondary,
                flexShrink: 0,
              }}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div style={{color: '#f87171', fontSize: 13}}>{aiResponse.error}</div>
                <button
                  onClick={() => setAiResponse(null)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: theme.textSecondary,
                    cursor: 'pointer',
                    fontSize: 16,
                    padding: '0 4px',
                  }}>
                  ×
                </button>
              </div>
            </div>
          )}

          {/* AI Cards Grid */}
          {(aiCards.length > 0 || aiCardsLoading) && (
            <div
              style={{
                padding: '12px 16px',
                margin: '0 16px 12px 16px',
                borderRadius: 4,
                background: theme.bgSecondary,
                flexShrink: 0,
              }}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
                <span style={{color: theme.textSecondary, fontSize: 11, textTransform: 'uppercase'}}>
                  Suggested Cards {aiCards.length > 0 && `(${aiCards.length})`}
                  {aiCardsLoading && ' - loading...'}
                </span>
                <button
                  onClick={() => { setAiCards([]); setHoveredAiCard(null) }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: theme.textSecondary,
                    cursor: 'pointer',
                    fontSize: 16,
                    padding: '0 4px',
                  }}>
                  ×
                </button>
              </div>

              {/* Cards Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 12,
              }}>
                {aiCards.map((card, idx) => {
                  // Build a card object compatible with addCardToDeck
                  // Must include source: 'scryfall' and all fields for proper caching
                  const deckCard = {
                    name: card.name,
                    url: card.image,
                    colors: card.scryfallData?.colors || [],
                    color_identity: card.scryfallData?.color_identity || [],
                    type_line: card.scryfallData?.type_line || '',
                    cmc: card.scryfallData?.cmc || 0,
                    mana_cost: card.scryfallData?.mana_cost || '',
                    rarity: card.scryfallData?.rarity || 'common',
                    scryfallId: card.scryfallData?.scryfallId || card.scryfallData?.id,
                    setCode: card.scryfallData?.set || '',
                    setName: card.scryfallData?.set_name || '',
                    collector_number: card.scryfallData?.collector_number || '',
                    prices: card.scryfallData?.prices || null,
                    card_faces: card.scryfallData?.card_faces || null,
                    oracle_text: card.scryfallData?.oracle_text || '',
                    // Mark as scryfall source so addCardToDeck will cache it
                    source: card.fromLocal ? 'local' : 'scryfall',
                  }
                  return (
                    <div
                      key={card.name + idx}
                      draggable
                      onDragStart={() => {
                        setDraggingCard({...deckCard, deckCardId: `new-${Date.now()}`, fromStackId: null})
                      }}
                      onDragEnd={() => {
                        setDraggingCard(null)
                        setDragOverStack(null)
                      }}
                      onMouseEnter={() => setHoveredAiCard(card.name)}
                      onMouseLeave={() => setHoveredAiCard(null)}
                      onClick={() => {
                        if (card.image) {
                          addCardToDeck(deckCard)
                        }
                        setHoveredAiCard(hoveredAiCard === card.name ? null : card.name)
                      }}
                      style={{
                        cursor: card.image ? 'grab' : 'pointer',
                        borderRadius: 4,
                        overflow: 'hidden',
                        border: hoveredAiCard === card.name ? `2px solid ${theme.text}` : '2px solid transparent',
                        transition: 'border-color 0.15s',
                      }}>
                      {card.image ? (
                        <img
                          src={card.image}
                          alt={card.name}
                          draggable={false}
                          style={{
                            width: '100%',
                            display: 'block',
                            borderRadius: 4,
                          }}
                        />
                      ) : (
                        <div style={{
                          aspectRatio: '488/680',
                          background: theme.bgTertiary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          color: theme.textSecondary,
                          padding: 8,
                          textAlign: 'center',
                        }}>
                          {card.name}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Hovered Card Explanation */}
              {hoveredAiCard && (
                <div style={{
                  marginTop: 12,
                  padding: '10px 12px',
                  background: theme.bgTertiary,
                  borderRadius: 4,
                  fontSize: 13,
                  color: theme.text,
                  lineHeight: 1.5,
                }}>
                  <strong>{hoveredAiCard}</strong>
                  <div style={{marginTop: 4}}>
                    {aiCards.find(c => c.name === hoveredAiCard)?.why}
                  </div>
                  {aiCards.find(c => c.name === hoveredAiCard)?.tags?.length > 0 && (
                    <div style={{marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap'}}>
                      {aiCards.find(c => c.name === hoveredAiCard).tags.map(tag => (
                        <span
                          key={tag}
                          style={{
                            padding: '2px 6px',
                            background: theme.bgSecondary,
                            borderRadius: 3,
                            fontSize: 10,
                            color: theme.textSecondary,
                          }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Search Results (if any) */}
          {deckSearchResults.length > 0 && (
            <div
              style={{
                padding: '8px 16px',
                borderBottom: `1px solid ${theme.border}`,
                maxHeight: 365,
                overflowY: 'auto',
                flexShrink: 0,
              }}>
              <div style={{color: theme.textSecondary, fontSize: 12, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span>{deckSearchResults.length} results - click or drag to add</span>
                <button
                  onClick={() => {
                    setDeckSearchResults([])
                    setDeckSearchQuery('')
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: theme.textSecondary,
                    cursor: 'pointer',
                    padding: '2px 6px',
                    fontSize: 21,
                    lineHeight: 1,
                    borderRadius: 3,
                  }}
                  title="Clear search results">
                  ×
                </button>
              </div>
              <div style={{display: 'flex', gap: 12, flexWrap: 'wrap'}}>
                {deckSearchResults.slice(0, 20).map((card, i) => (
                  <div
                    key={i}
                    draggable
                    onDragStart={() => {
                      // Add a temp deckCardId for dragging from search results
                      setDraggingCard({...card, deckCardId: `new-${Date.now()}`, fromStackId: null})
                    }}
                    onDragEnd={() => {
                      setDraggingCard(null)
                      setDragOverStack(null)
                    }}
                    onClick={() => addCardToDeck(card)}
                    style={{
                      width: 143,
                      cursor: 'grab',
                      opacity: 0.9,
                      transition: 'opacity 0.15s, transform 0.15s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.opacity = '1'
                      e.currentTarget.style.transform = 'scale(1.05)'
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.opacity = '0.9'
                      e.currentTarget.style.transform = 'scale(1)'
                    }}>
                    <img
                      src={card.url}
                      alt={card.name}
                      draggable={false}
                      style={{
                        width: '100%',
                        borderRadius: 8,
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Commander Drop Zone + Add Stack Button */}
          <div style={{padding: '8px 16px', flexShrink: 0, display: 'flex', gap: 12, alignItems: 'center'}}>
            {/* Commander Drop Zone */}
            <div
              onDragOver={e => {
                e.preventDefault()
                setDragOverCommander(true)
              }}
              onDragLeave={() => {
                setDragOverCommander(false)
              }}
              onDrop={e => {
                e.preventDefault()
                setDragOverCommander(false)
                if (draggingCard && draggingCard.fromStackId) {
                  setCommander(draggingCard, draggingCard.fromStackId)
                  setDraggingCard(null)
                }
              }}
              style={{
                padding: activeDeck?.commander ? '0 8px 0 0' : '8px 16px',
                background: dragOverCommander ? 'rgba(255, 204, 51, 0.1)' : theme.bgSecondary,
                border: dragOverCommander
                  ? '2px dashed #ffcc33'
                  : activeDeck?.commander
                  ? 'none'
                  : `2px dashed ${theme.border}`,
                borderRadius: 4,
                color: theme.text,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: 177,
                height: 36,
                overflow: 'hidden',
                transition: 'all 0.15s',
              }}>
              {activeDeck?.commander ? (
                <>
                  {/* Art crop - shows just the art portion of the card */}
                  <div
                    style={{
                      width: 40,
                      height: '100%',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}>
                    <img
                      src={activeDeck.commander.url}
                      alt={activeDeck.commander.name}
                      style={{
                        width: 70,
                        height: 98,
                        marginTop: -10,
                        marginLeft: -17,
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 12,
                      maxWidth: 100,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {activeDeck.commander.name}
                  </span>
                  <button
                    onClick={clearCommander}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: theme.textSecondary,
                      fontSize: 14,
                      cursor: 'pointer',
                      padding: '0 4px',
                      opacity: 0.6,
                    }}
                    title="Remove commander">
                    ×
                  </button>
                </>
              ) : (
                <span style={{color: theme.textSecondary}}>👑 Commander</span>
              )}
            </div>

            <button
              onClick={addStack}
              style={{
                padding: '0 16px',
                height: 36,
                background: theme.bgSecondary,
                border: 'none',
                borderRadius: 4,
                color: theme.text,
                fontSize: 13,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}>
              <span>+</span> Add Stack
            </button>
          </div>

          {/* Deck Stacks */}
          <div
            ref={stacksContainerRef}
            onMouseDown={e => {
              // Middle mouse button (button === 1)
              if (e.button === 1) {
                e.preventDefault()
                setIsPanning(true)
                setPanStart({x: e.clientX, y: e.clientY, scrollLeft: stacksContainerRef.current?.scrollLeft || 0, scrollTop: stacksContainerRef.current?.scrollTop || 0})
              }
            }}
            onMouseMove={e => {
              if (isPanning && stacksContainerRef.current) {
                const dy = e.clientY - panStart.y
                stacksContainerRef.current.scrollTop = panStart.scrollTop - dy
              }
            }}
            onMouseUp={e => {
              if (e.button === 1) setIsPanning(false)
            }}
            onMouseLeave={() => setIsPanning(false)}
            style={{
              overflowX: 'hidden',
              overflowY: 'auto',
              padding: '16px',
              width: '100%',
              boxSizing: 'border-box',
              position: 'relative',
              minHeight: masonryHeight || 'calc(100vh - 200px)',
              cursor: isPanning ? 'grabbing' : 'default',
              height: 'calc(100vh - 200px)',
            }}>
            {/* Drop position indicator for stack reordering */}
            {draggingStack !== null && dragOverStackPosition !== null && dragOverStackPosition !== draggingStack && (() => {
              // Calculate indicator position - use existing position or calculate "end" position
              const pos = masonryPositions[dragOverStackPosition]
              if (!pos && dragOverStackPosition !== activeDeck.stacks.length) return null

              // For end position, find shortest column
              let indicatorX = pos?.x || 0
              let indicatorY = pos?.y || 0
              if (!pos && masonryPositions.length > 0) {
                const containerWidth = stacksContainerRef.current?.clientWidth - 32 || 800
                const numColumns = Math.max(1, Math.floor((containerWidth + STACK_GAP) / (STACK_WIDTH + STACK_GAP)))
                const columnHeights = new Array(numColumns).fill(0)
                masonryPositions.forEach((p, i) => {
                  if (i !== draggingStack) {
                    const col = Math.round(p.x / (STACK_WIDTH + STACK_GAP))
                    const stackEl = stackRefs.current[activeDeck.stacks[i]?.id]
                    columnHeights[col] = Math.max(columnHeights[col], p.y + (stackEl?.offsetHeight || 400) + STACK_GAP)
                  }
                })
                const shortestCol = columnHeights.indexOf(Math.min(...columnHeights))
                indicatorX = shortestCol * (STACK_WIDTH + STACK_GAP)
                indicatorY = columnHeights[shortestCol]
              }

              return (
                <div
                  style={{
                    position: 'absolute',
                    left: indicatorX,
                    top: indicatorY,
                    width: STACK_WIDTH,
                    height: 100,
                    background: 'rgba(255, 204, 51, 0.15)',
                    border: '2px dashed #ffcc33',
                    borderRadius: 8,
                    boxShadow: '0 0 12px rgba(255, 204, 51, 0.3)',
                    pointerEvents: 'none',
                    zIndex: 100,
                    transition: 'left 0.15s ease, top 0.15s ease',
                  }}
                />
              )
            })()}
            {activeDeck.stacks.map((stack, stackIndex) => (
              <div
                key={stack.id}
                onDragOver={e => {
                  e.preventDefault()
                  if (draggingStack !== null) {
                    setDragOverStackPosition(stackIndex)
                  } else {
                    setDragOverStack(stack.id)
                  }
                }}
                onDragLeave={() => {
                  setDragOverStack(null)
                  setDragOverStackPosition(null)
                }}
                onDrop={e => {
                  e.preventDefault()
                  // Handle stack reordering
                  if (draggingStack !== null) {
                    reorderStack(draggingStack, stackIndex)
                    setDraggingStack(null)
                    setDragOverStackPosition(null)
                    return
                  }
                  // Handle card dropping
                  if (draggingCard) {
                    if (draggingCard.fromStackId === null) {
                      // Dragging from search results - add as new card to this stack (with caching)
                      addCardToStack(draggingCard, stack.id)
                    } else {
                      // Dragging between stacks
                      moveCard(draggingCard.deckCardId, draggingCard.fromStackId, stack.id)
                    }
                    setDraggingCard(null)
                    setDragOverStack(null)
                  }
                }}
                ref={el => stackRefs.current[stack.id] = el}
                style={{
                  position: 'absolute',
                  left: masonryPositions[stackIndex]?.x || 0,
                  top: (() => {
                    const baseTop = masonryPositions[stackIndex]?.y || 0
                    // If there's an expanded card in a stack above us in the same column, add offset
                    if (expandedDeckCard && masonryPositions[stackIndex]) {
                      const myColumn = Math.round((masonryPositions[stackIndex]?.x || 0) / (STACK_WIDTH + STACK_GAP))
                      const expandedStackIndex = activeDeck.stacks.findIndex(s => s.id === expandedDeckCard.stackId)
                      if (expandedStackIndex >= 0 && expandedStackIndex < stackIndex) {
                        const expandedColumn = Math.round((masonryPositions[expandedStackIndex]?.x || 0) / (STACK_WIDTH + STACK_GAP))
                        if (myColumn === expandedColumn) {
                          // Only push down if NOT the last card in the stack
                          const expandedStack = activeDeck.stacks[expandedStackIndex]
                          const isLastCard = expandedDeckCard.cardIndex === expandedStack.cards.length - 1
                          if (!isLastCard) {
                            return baseTop + 210 // Push down when card above is expanded
                          }
                        }
                      }
                    }
                    return baseTop
                  })(),
                  width: STACK_WIDTH,
                  transition: draggingStack === null ? 'left 0.2s ease, top 0.2s ease' : 'none',
                }}>
                {/* Stack content */}
                <div
                  style={{
                    width: '100%',
                    minHeight: Math.round((isMobile ? 300 : 305) * deckCardScale),
                    background: dragOverStack === stack.id ? 'rgba(74, 158, 255, 0.2)' : theme.bgSecondary,
                    borderRadius: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s ease',
                    border: dragOverStack === stack.id ? '2px dashed #4a9eff' : '2px solid transparent',
                    opacity: draggingStack === stackIndex ? 0.4 : 1,
                  }}>
                  {/* Stack Header - draggable to reorder */}
                  <div
                    draggable
                    onDragStart={e => {
                      setDraggingStack(stackIndex)
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                    onDragEnd={() => {
                      setDraggingStack(null)
                      setDragOverStackPosition(null)
                    }}
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${theme.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 4,
                      cursor: draggingStack === stackIndex ? 'grabbing' : 'grab',
                    }}>
                    {/* Drag handle */}
                    <span
                      style={{
                        color: theme.textSecondary,
                        fontSize: 10,
                        marginRight: 4,
                        cursor: draggingStack === stackIndex ? 'grabbing' : 'grab',
                        padding: '4px 2px',
                        marginLeft: -2,
                      }}>
                      ⋮⋮
                    </span>
                    {editingStackId === stack.id ? (
                      <input
                        type="text"
                        value={stack.name}
                        onChange={e => renameStack(stack.id, e.target.value)}
                        onFocus={e => e.target.select()}
                        onBlur={() => setEditingStackId(null)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === 'Escape') {
                            setEditingStackId(null)
                          }
                        }}
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                        draggable={false}
                        autoFocus
                        spellCheck={false}
                        style={{
                          flex: 1,
                          padding: '4px 6px',
                          fontSize: 13,
                          fontWeight: 500,
                          border: 'none',
                          borderRadius: 3,
                          background: theme.bgSecondary,
                          color: theme.text,
                          outline: 'none',
                          minWidth: 0,
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          flex: 1,
                          fontSize: 13,
                          fontWeight: 500,
                          color: theme.text,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          padding: '4px 6px',
                        }}>
                        {stack.name}
                      </span>
                    )}
                    {/* Stack price */}
                    {(() => {
                      const stackTotal = stack.cards.reduce((sum, c) => sum + parseFloat(c.prices?.usd || 0), 0)
                      return stackTotal > 0 ? (
                        <span style={{color: '#4ade80', fontSize: 10, flexShrink: 0, marginRight: 4}}>${stackTotal.toFixed(2)}</span>
                      ) : null
                    })()}
                    <span style={{color: theme.textSecondary, fontSize: 11, flexShrink: 0}}>{stack.cards.length}</span>
                    {/* Stack menu button */}
                    <div style={{position: 'relative'}}>
                      <button
                        onClick={e => {
                          e.stopPropagation()
                          setStackMenuOpen(stackMenuOpen === stack.id ? null : stack.id)
                        }}
                        onMouseDown={e => e.stopPropagation()}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: theme.textSecondary,
                          fontSize: 14,
                          cursor: 'pointer',
                          padding: '2px 6px',
                          opacity: 0.6,
                          lineHeight: 1,
                          letterSpacing: 1,
                        }}
                        title="Stack options">
                        ⋯
                      </button>
                      {/* Dropdown menu */}
                      {stackMenuOpen === stack.id && (
                        <>
                          {/* Backdrop to close menu */}
                          <div
                            onClick={() => setStackMenuOpen(null)}
                            style={{
                              position: 'fixed',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              zIndex: 99,
                            }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              background: theme.bgSecondary,
                              border: `1px solid ${theme.border}`,
                              borderRadius: 6,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                              zIndex: 100,
                              minWidth: 160,
                              overflow: 'hidden',
                            }}>
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              setStackMenuOpen(null)
                              setEditingStackId(stack.id)
                            }}
                            style={{
                              display: 'block',
                              width: '100%',
                              padding: '10px 14px',
                              background: 'none',
                              border: 'none',
                              color: theme.text,
                              fontSize: 13,
                              textAlign: 'left',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={e => (e.target.style.background = theme.bg)}
                            onMouseLeave={e => (e.target.style.background = 'none')}>
                            Rename stack
                          </button>
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              const cardNames = stack.cards.map(c => c.name).join('\n')
                              navigator.clipboard.writeText(cardNames)
                              setStackMenuOpen(null)
                            }}
                            style={{
                              display: 'block',
                              width: '100%',
                              padding: '10px 14px',
                              background: 'none',
                              border: 'none',
                              color: theme.text,
                              fontSize: 13,
                              textAlign: 'left',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={e => (e.target.style.background = theme.bg)}
                            onMouseLeave={e => (e.target.style.background = 'none')}>
                            Copy card names
                          </button>
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              setStackMenuOpen(null)
                              deleteStack(stack.id)
                            }}
                            style={{
                              display: 'block',
                              width: '100%',
                              padding: '10px 14px',
                              background: 'none',
                              border: 'none',
                              color: '#f55',
                              fontSize: 13,
                              textAlign: 'left',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={e => (e.target.style.background = theme.bg)}
                            onMouseLeave={e => (e.target.style.background = 'none')}>
                            Delete stack
                          </button>
                        </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Stack Cards */}
                  <div
                    style={{
                      padding: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      paddingBottom: 4,
                    }}>
                    {(() => {
                      // Sort cards based on stackSortOrder
                      let sortedCards = [...stack.cards]
                      if (stackSortOrder === 'cmc') {
                        sortedCards.sort((a, b) => (a.cmc || 0) - (b.cmc || 0))
                      } else if (stackSortOrder === 'price') {
                        sortedCards.sort((a, b) => parseFloat(a.prices?.usd || 0) - parseFloat(b.prices?.usd || 0))
                      }
                      return sortedCards
                    })().map((card, cardIndex) => {
                      // Deck stack fanning - similar to main page stacked view
                      const deckStackVisible = Math.round((isMobile ? 44 : 28) * deckCardScale)
                      const deckCardWidth = Math.round((isMobile ? 142 : 182) * deckCardScale) // fill more of stack width
                      const deckCardHeight = Math.round((deckCardWidth * 680) / 488)
                      const isExpanded = expandedDeckCard?.stackId === stack.id && expandedDeckCard?.cardIndex === cardIndex
                      const prevExpanded =
                        expandedDeckCard?.stackId === stack.id && expandedDeckCard?.cardIndex === cardIndex - 1
                      const marginTop = cardIndex === 0 ? 0 : prevExpanded ? 0 : -(deckCardHeight - deckStackVisible)

                      return (
                        <div
                          key={card.deckCardId}
                          draggable
                          onDragStart={() => {
                            didDragRef.current = true
                            setDraggingCard({...card, fromStackId: stack.id})
                            setExpandedDeckCard(null)
                          }}
                          onDragEnd={() => {
                            setDraggingCard(null)
                            setDragOverStack(null)
                            // Reset drag flag after a short delay (after onClick would fire)
                            setTimeout(() => { didDragRef.current = false }, 100)
                          }}
                          onMouseEnter={() => setExpandedDeckCard({stackId: stack.id, cardIndex})}
                          onMouseLeave={() => setExpandedDeckCard(null)}
                          onClick={(e) => {
                            // Only open stats if not dragging
                            if (!didDragRef.current) {
                              e.stopPropagation()
                              setStatsCard(card)
                              setStatsCardSource({stackId: stack.id, deckCardId: card.deckCardId})
                            }
                          }}
                          style={{
                            position: 'relative',
                            cursor: 'grab',
                            marginTop,
                            transition: 'margin-top 0.2s ease, padding-bottom 0.2s ease',
                            zIndex: isExpanded ? 10 : 1,
                            paddingBottom: isExpanded && cardIndex < stack.cards.length - 1 ? 18 : 0,
                          }}>
                          <img
                            src={card.url}
                            alt={card.name}
                            style={{
                              width: deckCardWidth,
                              height: deckCardHeight,
                              borderRadius: Math.round(8 * deckCardScale),
                              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                              display: 'block',
                            }}
                          />
                          {/* Price display - show when expanded */}
                          {isExpanded && (
                            <div
                              style={{
                                position: 'absolute',
                                bottom: cardIndex < stack.cards.length - 1 ? 5 : -13,
                                left: 0,
                                right: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 8,
                                fontSize: 9,
                                color: theme.textSecondary,
                              }}>
                              <span>
                                TCG:{' '}
                                <span style={{color: card.prices?.usd ? '#4ade80' : theme.textSecondary}}>
                                  {card.prices?.usd ? `$${card.prices.usd}` : '--'}
                                </span>
                              </span>
                              <span>
                                CM:{' '}
                                <span style={{color: card.prices?.eur ? theme.text : theme.textSecondary}}>
                                  {card.prices?.eur ? `€${card.prices.eur}` : '--'}
                                </span>
                              </span>
                            </div>
                          )}
                          {/* Remove button on hover */}
                          {isExpanded && (
                            <button
                              onClick={e => {
                                e.stopPropagation()
                                removeCardFromDeck(stack.id, card.deckCardId)
                              }}
                              style={{
                                position: 'absolute',
                                top: 4,
                                right: isMobile ? 4 : 12,
                                width: 20,
                                height: 20,
                                background: 'rgba(0,0,0,0.7)',
                                border: 'none',
                                borderRadius: 4,
                                color: '#fff',
                                fontSize: 12,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}>
                              ×
                            </button>
                          )}
                        </div>
                      )
                    })}
                    {stack.cards.length === 0 && (
                      <div
                        style={{
                          padding: 16,
                          textAlign: 'center',
                          color: theme.textSecondary,
                          fontSize: 12,
                        }}>
                        Drop cards here
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {/* Drop zone after all stacks */}
            {draggingStack !== null && (
              <div
                onDragOver={e => {
                  e.preventDefault()
                  setDragOverStackPosition(activeDeck.stacks.length)
                }}
                onDragLeave={() => setDragOverStackPosition(null)}
                onDrop={e => {
                  e.preventDefault()
                  reorderStack(draggingStack, activeDeck.stacks.length)
                  setDraggingStack(null)
                  setDragOverStackPosition(null)
                }}
                style={{
                  minWidth: 40,
                  height: '100%',
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Previews
